/* Wix page checker. A store owner pastes the source of one live Wix page
   ("View page source"), and the checks below say what Google can read on it
   and which apps and Custom Code the site loads.

   Everything runs in the browser. The source is read with DOMParser, which
   gives an inert document: nothing in it runs, loads or is shown, and it is
   never added to this page. The checks only read tags, attributes and text,
   always across the whole document: a merchant's <noscript><img> pixel in
   <head> makes the parser end the head early and move the title, canonical
   and structured data into <body> (seen on 3 of 21 real Wix pages).

   The checks are plain functions, so the tests and the server-rendered first
   paint (wix-page-checker.cjs) run the code the browser runs. The server has
   no DOMParser, so it reads the made-up sample with parseLite() below; the
   tests check that both readers report the same findings on real Wix pages.
   Facts about Wix and Google come from the research sheets of 2026-10-02 and
   each one names its source in SOURCES. */
(function (root, factory) {
  'use strict';
  const inNode = typeof require === 'function' && typeof module === 'object' && module && module.exports;
  const shared = inNode ? require('./wix-shared.js') : root.WixShared;
  const catalog = inNode ? require('./wix-app-catalog.js') : root.WixAppCatalog;
  const api = factory(shared, catalog);
  if (inNode) module.exports = api;
  else root.WixPageChecker = api;
  if (typeof document !== 'undefined' && typeof document.querySelector === 'function') {
    const start = () => api.init(document.querySelector('[data-wpc-lab]'));
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function (shared, catalog) {
  'use strict';

  const PATH = '/tools/wix-page-checker';
  const META = Object.freeze({
    path: PATH,
    name: 'Wix page checker',
    summary: 'Paste a page’s source. See what Google can read on it, plus the apps installed on your site and its Custom Code.'
  });
  const ORIGIN = 'https://myuser.ai';
  const MAX_BYTES = 8 * 1024 * 1024;
  const TITLE_PX = 600;

  const GROUPS = Object.freeze([
    { id: 'index', title: 'Can Google index it?', short: 'Indexing' },
    { id: 'search', title: 'How it shows in Google', short: 'Search result' },
    { id: 'page', title: 'What is on the page', short: 'Page content' },
    { id: 'data', title: 'Structured data', short: 'Structured data' },
    { id: 'apps', title: 'Apps and outside scripts', short: 'Apps and scripts' }
  ]);
  const STATUS_ORDER = { fix: 0, check: 1, info: 2, fine: 3 };
  const STATUS_WORDS = { fix: 'Fix', check: 'Check', info: 'Note', fine: 'Fine' };

  // Where each fact comes from. A finding lists the keys behind its "why".
  const SOURCES = {
    'wix-source': ['Wix: Viewing your page’s source code the way search engines do', 'https://support.wix.com/en/article/viewing-your-pages-source-code-the-way-search-engines-do'],
    'wix-seo': ['Wix: Search engine optimization (SEO)', 'https://support.wix.com/en/article/search-engine-optimization-seo'],
    'wix-seo-panel': ['Wix: Customizing your page’s SEO settings in the SEO panel', 'https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel'],
    'wix-titles': ['Wix: Adding SEO title tags and meta descriptions to your pages', 'https://support.wix.com/en/article/adding-seo-title-tags-and-meta-descriptions-to-your-pages'],
    'wix-defaults': ['Wix: Understanding your site’s default SEO settings', 'https://support.wix.com/en/article/understanding-your-sites-default-seo-settings-6447365'],
    'wix-patterns': ['Wix: Customizing your SEO settings', 'https://support.wix.com/en/article/customizing-your-seo-settings-1420929'],
    'wix-product-seo': ['Wix Stores: Optimizing your SEO for product pages', 'https://support.wix.com/en/article/wix-stores-optimizing-your-seo-for-product-pages'],
    'wix-category-seo': ['Wix Stores: SEO for category pages', 'https://support.wix.com/en/article/wix-stores-seo-for-category-pages'],
    'wix-home-title': ['Wix: Setting the homepage’s title for search results', 'https://support.wix.com/en/article/wix-seo-setup-checklist-setting-the-homepages-title-for-search-results'],
    'wix-meta-hub': ['Wix SEO Hub: What is a meta description?', 'https://www.wix.com/seo/learn/resource/what-is-a-meta-description'],
    'wix-noindex': ['Wix Editor: Preventing search engines from indexing your page', 'https://support.wix.com/en/article/wix-editor-preventing-search-engines-from-indexing-your-page'],
    'wix-index-site': ['Wix: Enabling search engines like Google to index your site', 'https://support.wix.com/en/article/enabling-search-engines-like-google-to-index-your-site'],
    'wix-robots': ['Wix: Using the robots meta tag', 'https://support.wix.com/en/article/using-the-robots-meta-tag'],
    'wix-canonical': ['Wix: Changing the canonical tags for your site’s pages', 'https://support.wix.com/en/article/changing-the-canonical-tags-for-your-sites-pages'],
    'wix-free-url': ['Wix: Removing your free Wix URL after connecting your domain', 'https://support.wix.com/en/article/request-removing-your-free-wix-url-after-connecting-your-domain'],
    'wix-https': ['Wix: About SSL and HTTPS', 'https://support.wix.com/en/article/about-ssl-and-https'],
    'wix-gsc': ['Wix: Connecting your site to Google', 'https://support.wix.com/en/article/connecting-your-site-to-google-in-the-wix-seo-wiz'],
    'wix-not-found': ['Wix: Site or pages not appearing in search results', 'https://support.wix.com/en/article/site-or-pages-not-appearing-in-search-engine-results'],
    'wix-language': ['Wix: Changing your site’s regional settings', 'https://support.wix.com/en/article/changing-your-sites-regional-settings'],
    'wix-headings': ['Wix: Managing your page’s heading tags', 'https://support.wix.com/en/article/managing-your-pages-heading-tags'],
    'wix-structure': ['Wix: Accessibility: preparing your site structure', 'https://support.wix.com/en/article/accessibility-preparing-your-site-structure'],
    'wix-h1-hub': ['Wix SEO Hub: Header tags', 'https://www.wix.com/seo/learn/resource/header-tags'],
    'wix-alt-auto': ['Wix Stores: Automatically generating alt text for product images', 'https://support.wix.com/en/article/wix-stores-request-automatically-generating-alt-text-for-product-images'],
    'wix-media-url': ['Wix: Editing the URL of a media file', 'https://support.wix.com/en/article/wix-media-request-editing-the-url-of-a-media-file'],
    'wix-lazy': ['Wix: How Wix improves a site’s loading time', 'https://support.wix.com/en/article/site-performance-how-wix-improves-a-sites-loading-time'],
    'wix-social': ['Wix: Customizing the social share settings for your pages', 'https://support.wix.com/en/article/customizing-the-social-share-settings-for-your-sites-pages'],
    'wix-website-settings': ['Wix: Managing your website settings', 'https://support.wix.com/en/article/managing-your-website-settings'],
    'wix-markup': ['Wix: Adding structured data markup to your site’s pages', 'https://support.wix.com/en/article/adding-structured-data-markup-to-your-sites-pages-2546962'],
    'wix-presets': ['Wix: Understanding preset markups for your site’s pages', 'https://support.wix.com/en/article/understanding-preset-markups-for-your-sites-pages'],
    'wix-markup-trouble': ['Wix: Troubleshooting your structured data markup', 'https://support.wix.com/en/article/troubleshooting-your-structured-data-markup'],
    'wix-local-business': ['Wix: Creating Local Business markup for your site’s homepage', 'https://support.wix.com/en/article/creating-local-business-markup-for-your-sites-homepage'],
    'wix-speed': ['Wix: Site performance best practices', 'https://support.wix.com/en/article/site-performance-best-practices'],
    'wix-unused-js': ['Wix: Removing unused JavaScript', 'https://support.wix.com/en/article/site-performance-removing-unused-javascript'],
    'wix-cwv': ['Wix: About Core Web Vitals', 'https://support.wix.com/en/article/site-performance-about-core-web-vitals'],
    'wix-delete-apps': ['Wix: Deleting apps', 'https://support.wix.com/en/article/deleting-apps'],
    'wix-custom-code': ['Wix: Embedding custom code on your site', 'https://support.wix.com/en/article/wix-editor-embedding-custom-code-on-your-site'],
    'wix-custom-code-dev': ['Wix developers: About custom code', 'https://dev.wix.com/docs/develop-websites-sdk/code-your-site/build-a-custom-frontend/custom-code/about-custom-code'],
    'wix-integrations': ['Wix: About Marketing Integrations', 'https://support.wix.com/en/article/about-marketing-integrations'],
    'wix-embedded-scripts': ['Wix developers: About embedded scripts', 'https://dev.wix.com/docs/build-apps/develop-your-app/extensions/site-extensions/embedded-scripts/about-embedded-scripts'],
    'wix-speed-blog': ['Wix blog: Website speed optimization', 'https://www.wix.com/blog/website-speed-optimization'],
    'google-title': ['Google Search Central: title links', 'https://developers.google.com/search/docs/appearance/title-link'],
    'google-snippet': ['Google Search Central: snippets and meta descriptions', 'https://developers.google.com/search/docs/appearance/snippet'],
    'google-indexing': ['Google Search Console Help: Page indexing report', 'https://support.google.com/webmasters/answer/7440203'],
    'google-canonical': ['Google Search Central: canonical pages', 'https://developers.google.com/search/docs/crawling-indexing/canonicalization'],
    'google-links': ['Google Search Central: links Google can follow', 'https://developers.google.com/search/docs/crawling-indexing/links-crawlable'],
    'google-hreflang': ['Google Search Central: language versions of a page', 'https://developers.google.com/search/docs/specialty/international/localized-versions'],
    'google-merchant': ['Google Search Central: merchant listing structured data', 'https://developers.google.com/search/docs/appearance/structured-data/merchant-listing'],
    'google-sd-policies': ['Google Search Central: general structured data guidelines', 'https://developers.google.com/search/docs/appearance/structured-data/sd-policies'],
    'google-local-business': ['Google Search Central: local business structured data', 'https://developers.google.com/search/docs/appearance/structured-data/local-business'],
    'google-breadcrumb': ['Google Search Central: breadcrumb structured data', 'https://developers.google.com/search/docs/appearance/structured-data/breadcrumb'],
    'google-updates': ['Google Search Central: documentation updates', 'https://developers.google.com/search/updates'],
    'google-mc-markup': ['Google Merchant Center Help: structured data for products', 'https://support.google.com/merchants/answer/7331077'],
    'schema-availability': ['Schema.org: ItemAvailability', 'https://schema.org/ItemAvailability']
  };

  // Our guides and tools for the next step. Plain-text summaries print the full address.
  const GUIDES = {
    indexing: ['Why Wix pages aren’t indexed', '/blog/wix-pages-not-indexed'],
    titles: ['Wix titles and meta descriptions', '/blog/wix-seo-titles-descriptions'],
    returns: ['Return and shipping markup on Wix', '/blog/wix-return-shipping-markup'],
    speed: ['Wix apps and site speed', '/blog/wix-apps-site-speed'],
    leftover: ['Finding code left by removed apps', '/blog/wix-app-leftover-code'],
    popups: ['Wix pop-ups and Google', '/blog/wix-popups-and-google'],
    redirects: ['301 redirects on Wix', '/blog/wix-301-redirects'],
    policyTool: ['Return and shipping policy markup builder', '/tools/wix-policy-markup'],
    patternTool: ['SEO title and description preview', '/tools/wix-seo-pattern-preview']
  };
  const PSI = 'https://pagespeed.web.dev/';
  const RICH_TEST = 'https://search.google.com/test/rich-results';

  // ---- Small helpers -----------------------------------------------------------

  const esc = shared.esc;
  const tidy = value => shared.tidyText(value);
  const lower = value => (value == null ? '' : String(value).trim().toLowerCase());
  const plural = (count, one, many = `${one}s`) => `${count.toLocaleString('en-US')} ${count === 1 ? one : many}`;
  const NUMBER_WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];
  const countWord = count => NUMBER_WORDS[count] || count.toLocaleString('en-US');
  const joinWords = words => (words.length < 2 ? words.join('') : `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}`);
  const clip = (value, max = 90) => {
    const parts = shared.graphemes(String(value));
    return parts.length > max ? `${parts.slice(0, max - 1).join('').trimEnd()}…` : String(value);
  };
  const graphemeCount = value => shared.graphemes(String(value)).length;
  const asArray = value => (Array.isArray(value) ? value : value == null ? [] : [value]);
  const capital = value => value.charAt(0).toUpperCase() + value.slice(1);

  function utf8Bytes(text) {
    if (typeof TextEncoder === 'function') return new TextEncoder().encode(text).length;
    let bytes = 0;
    for (let i = 0; i < text.length; i += 1) {
      const code = text.charCodeAt(i);
      if (code < 0x80) bytes += 1;
      else if (code < 0x800) bytes += 2;
      else if (code >= 0xd800 && code <= 0xdbff && i + 1 < text.length) { bytes += 4; i += 1; } else bytes += 3;
    }
    return bytes;
  }

  function sizeLabel(bytes) {
    if (bytes < 1024) return bytes ? 'under 1 KB' : '0 KB';
    if (bytes < 1000 * 1024) return `${Math.round(bytes / 1024).toLocaleString('en-US')} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // For the size limit message: two decimals, so a file just over 8 MB
  // never reads as "8.0 MB".
  function overLimitLabel(bytes) {
    const shown = (bytes / (1024 * 1024)).toFixed(2);
    return Number(shown) <= MAX_BYTES / (1024 * 1024) ? 'just over 8 MB' : `${shown} MB`;
  }

  // HTML character references, for the small reader below. The browser's
  // DOMParser decodes everything itself.
  const ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', copy: '©', reg: '®', trade: '™', hellip: '…', mdash: '—', ndash: '–', lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”', laquo: '«', raquo: '»', bull: '•', middot: '·', deg: '°', euro: '€', pound: '£', yen: '¥', cent: '¢', times: '×', divide: '÷', eacute: 'é', egrave: 'è', agrave: 'à', aacute: 'á', ccedil: 'ç', uuml: 'ü', ouml: 'ö', auml: 'ä', szlig: 'ß', ntilde: 'ñ', iacute: 'í', oacute: 'ó', uacute: 'ú' };
  function decodeEntities(text) {
    if (text.indexOf('&') === -1) return text;
    return text.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z][a-z0-9]*);/gi, (match, body) => {
      if (body[0] === '#') {
        const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
        try { return code ? String.fromCodePoint(code) : match; } catch (_) { return match; }
      }
      const named = ENTITIES[body.toLowerCase()];
      return named === undefined ? match : named;
    });
  }

  // ---- A small HTML reader for the server's first paint ------------------------

  const VOID = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'keygen', 'link', 'meta', 'param', 'source', 'track', 'wbr']);
  const RAW = new Set(['script', 'style', 'xmp', 'iframe', 'noembed', 'noframes', 'textarea', 'title']);
  const RCDATA = new Set(['textarea', 'title']);
  const isSpace = code => code === 32 || code === 9 || code === 10 || code === 12 || code === 13;
  const isLetter = code => (code >= 65 && code <= 90) || (code >= 97 && code <= 122);

  // The index of the "</name" that closes a raw-text element, or the end.
  function findClose(html, name, from) {
    let at = from;
    for (;;) {
      const lt = html.indexOf('</', at);
      if (lt === -1) return html.length;
      const after = html.charCodeAt(lt + 2 + name.length);
      if (html.substr(lt + 2, name.length).toLowerCase() === name && (Number.isNaN(after) || isSpace(after) || after === 62 || after === 47)) return lt;
      at = lt + 2;
    }
  }

  function readTag(html, at) {
    const n = html.length;
    let i = at;
    while (i < n) {
      const code = html.charCodeAt(i);
      if (isSpace(code) || code === 62 || code === 47) break;
      i += 1;
    }
    const name = html.slice(at, i).toLowerCase();
    const attrs = Object.create(null);
    for (;;) {
      while (i < n && isSpace(html.charCodeAt(i))) i += 1;
      if (i >= n) return { name, attrs, end: n, selfClosing: false };
      const code = html.charCodeAt(i);
      if (code === 62) return { name, attrs, end: i + 1, selfClosing: false };
      if (code === 47) {
        if (html.charCodeAt(i + 1) === 62) return { name, attrs, end: i + 2, selfClosing: true };
        i += 1;
        continue;
      }
      const start = i;
      while (i < n) {
        const c = html.charCodeAt(i);
        if (isSpace(c) || c === 61 || c === 62 || (c === 47 && html.charCodeAt(i + 1) === 62)) break;
        i += 1;
      }
      const key = html.slice(start, i).toLowerCase();
      while (i < n && isSpace(html.charCodeAt(i))) i += 1;
      let value = '';
      if (html.charCodeAt(i) === 61) {
        i += 1;
        while (i < n && isSpace(html.charCodeAt(i))) i += 1;
        const quote = html[i];
        if (quote === '"' || quote === "'") {
          const end = html.indexOf(quote, i + 1);
          value = html.slice(i + 1, end === -1 ? n : end);
          i = end === -1 ? n : end + 1;
        } else {
          const valueStart = i;
          while (i < n && !isSpace(html.charCodeAt(i)) && html.charCodeAt(i) !== 62) i += 1;
          value = html.slice(valueStart, i);
        }
      }
      if (!key) { i = Math.max(i, start + 1); continue; }
      if (!(key in attrs)) attrs[key] = decodeEntities(value);
    }
  }

  function parseLite(source) {
    const html = String(source);
    const n = html.length;
    const root = { tag: '#root', attrs: Object.create(null), parent: null, start: 0, end: n, seq: 0 };
    const byTag = new Map();
    const stack = [root];
    let seq = 0;
    let i = 0;
    const add = node => {
      let list = byTag.get(node.tag);
      if (!list) byTag.set(node.tag, (list = []));
      list.push(node);
    };
    while (i < n) {
      const lt = html.indexOf('<', i);
      if (lt === -1) break;
      const next = html.charCodeAt(lt + 1);
      if (next === 33) {
        if (html.startsWith('<!--', lt)) {
          const end = html.indexOf('-->', lt + 4);
          i = end === -1 ? n : end + 3;
        } else {
          const end = html.indexOf('>', lt + 2);
          i = end === -1 ? n : end + 1;
        }
        continue;
      }
      if (next === 63) {
        const end = html.indexOf('>', lt + 2);
        i = end === -1 ? n : end + 1;
        continue;
      }
      if (next === 47) {
        let j = lt + 2;
        while (j < n && isLetter(html.charCodeAt(j))) j += 1;
        while (j < n && !isSpace(html.charCodeAt(j)) && html.charCodeAt(j) !== 62) j += 1;
        const name = html.slice(lt + 2, j).toLowerCase();
        const gt = html.indexOf('>', j);
        for (let k = stack.length - 1; k > 0; k -= 1) {
          if (stack[k].tag === name) {
            for (let m = stack.length - 1; m >= k; m -= 1) stack[m].end = lt;
            stack.length = k;
            break;
          }
        }
        i = gt === -1 ? n : gt + 1;
        continue;
      }
      if (!isLetter(next)) { i = lt + 1; continue; }
      const tag = readTag(html, lt + 1);
      if (tag.name === 'template') {
        const close = findClose(html, 'template', tag.end);
        const gt = html.indexOf('>', close);
        i = close >= n || gt === -1 ? n : gt + 1;
        continue;
      }
      seq += 1;
      const node = { tag: tag.name, attrs: tag.attrs, parent: stack[stack.length - 1], start: tag.end, end: tag.end, seq };
      add(node);
      if (RAW.has(tag.name)) {
        const close = findClose(html, tag.name, tag.end);
        node.end = close;
        node.raw = true;
        const gt = close >= n ? -1 : html.indexOf('>', close);
        i = gt === -1 ? n : gt + 1;
        continue;
      }
      if (!VOID.has(tag.name) && !tag.selfClosing) stack.push(node);
      i = tag.end;
    }
    for (let k = stack.length - 1; k > 0; k -= 1) stack[k].end = n;
    return { html, byTag, root };
  }

  function liteText(html, node) {
    const inner = html.slice(node.start, node.end);
    if (node.raw) return RCDATA.has(node.tag) ? decodeEntities(inner) : inner;
    let text = inner;
    if (text.indexOf('<!--') !== -1) {
      let out = '';
      let at = 0;
      for (;;) {
        const open = text.indexOf('<!--', at);
        if (open === -1) { out += text.slice(at); break; }
        out += text.slice(at, open);
        const close = text.indexOf('-->', open + 4);
        if (close === -1) break;
        at = close + 3;
      }
      text = out;
    }
    return decodeEntities(text.replace(/<\/?[a-zA-Z][^>]*>/g, ''));
  }

  // Both readers answer the same few questions, so every check runs on either.
  function liteView(doc) {
    return {
      elements: tag => doc.byTag.get(tag) || [],
      select: tags => tags.reduce((all, tag) => all.concat(doc.byTag.get(tag) || []), []).sort((a, b) => a.seq - b.seq),
      attr: (node, name) => (name in node.attrs ? node.attrs[name] : null),
      text: node => liteText(doc.html, node),
      parent: node => (node.parent && node.parent.tag !== '#root' ? node.parent : null),
      tag: node => node.tag,
      rootAttr: name => { const html = (doc.byTag.get('html') || [])[0]; return html && name in html.attrs ? html.attrs[name] : null; },
      bodyAttr: name => { const body = (doc.byTag.get('body') || [])[0]; return body && name in body.attrs ? body.attrs[name] : null; }
    };
  }

  function domView(doc) {
    return {
      elements: tag => Array.from(doc.getElementsByTagName(tag)),
      select: tags => Array.from(doc.querySelectorAll(tags.join(','))),
      attr: (node, name) => node.getAttribute(name),
      text: node => node.textContent || '',
      parent: node => node.parentElement,
      tag: node => node.localName,
      rootAttr: name => (doc.documentElement ? doc.documentElement.getAttribute(name) : null),
      bodyAttr: name => (doc.body ? doc.body.getAttribute(name) : null)
    };
  }

  function defaultReader() {
    if (typeof DOMParser === 'function') return html => domView(new DOMParser().parseFromString(html, 'text/html'));
    return html => liteView(parseLite(html));
  }

  // ---- Reading the facts the checks need -----------------------------------------

  const PIXEL_HOSTS = /(^|\.)(facebook\.com|google-analytics\.com|googletagmanager\.com|bat\.bing\.com|ct\.pinterest\.com|analytics\.tiktok\.com|px\.ads\.linkedin\.com|tr\.snapchat\.com|t\.co|analytics\.twitter\.com)$/i;

  function hostOf(value, base) {
    try { return new URL(value, base || 'https://page.invalid/').hostname.toLowerCase(); } catch (_) { return ''; }
  }

  function readJson(text) {
    try { return JSON.parse(text); } catch (_) { return null; }
  }

  function collect(view) {
    const up = (node, tags) => {
      for (let p = view.parent(node); p; p = view.parent(p)) if (tags.indexOf(view.tag(p)) !== -1) return p;
      return null;
    };
    const upHook = (node, pattern) => {
      for (let p = view.parent(node); p; p = view.parent(p)) {
        const hook = view.attr(p, 'data-hook');
        if (hook && pattern.test(hook)) return true;
      }
      return false;
    };
    const facts = {
      lang: view.rootAttr('lang'),
      bodyClass: view.bodyAttr('class') || '',
      titles: [],
      metas: [],
      links: [],
      headings: [],
      images: [],
      anchors: [],
      paragraphs: [],
      scripts: [],
      ldjson: [],
      modelText: null,
      essentialText: null,
      errorMarker: false
    };
    view.elements('title').forEach(node => {
      if (!up(node, ['svg', 'math'])) facts.titles.push(tidy(view.text(node)));
    });
    view.elements('meta').forEach(node => {
      facts.metas.push({
        name: lower(view.attr(node, 'name')),
        property: lower(view.attr(node, 'property')),
        httpEquiv: lower(view.attr(node, 'http-equiv')),
        content: view.attr(node, 'content')
      });
    });
    view.elements('link').forEach(node => {
      facts.links.push({
        rel: lower(view.attr(node, 'rel')).split(/\s+/).filter(Boolean),
        href: (view.attr(node, 'href') || '').trim(),
        hreflang: view.attr(node, 'hreflang'),
        as: lower(view.attr(node, 'as')),
        type: lower(view.attr(node, 'type'))
      });
    });
    view.select(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']).forEach(node => {
      if (up(node, ['svg', 'math', 'noscript'])) return;
      facts.headings.push({ level: Number(view.tag(node).charAt(1)), text: tidy(view.text(node)), hook: view.attr(node, 'data-hook') || '' });
    });
    // Body copy, for template text such as "I'm a paragraph". Only the start
    // of each paragraph is kept: the sample sentences open the text.
    view.elements('p').forEach(node => {
      if (up(node, ['svg', 'math', 'noscript'])) return;
      const text = tidy(view.text(node));
      if (text) facts.paragraphs.push(text.slice(0, 400));
    });
    const anchorImages = new Map();
    view.elements('img').forEach(node => {
      if (up(node, ['svg', 'math'])) return;
      const link = up(node, ['a']);
      const image = {
        src: (view.attr(node, 'src') || '').trim(),
        srcset: view.attr(node, 'srcset') || '',
        alt: view.attr(node, 'alt'),
        width: view.attr(node, 'width'),
        height: view.attr(node, 'height'),
        style: view.attr(node, 'style') || '',
        hook: view.attr(node, 'data-hook') || '',
        noscript: Boolean(up(node, ['noscript'])),
        link: link ? (view.attr(link, 'href') || '') : null,
        productCard: upHook(node, /product/i),
        grid: upHook(node, /product-item|slider/i)
      };
      facts.images.push(image);
      if (link) {
        const list = anchorImages.get(link) || [];
        list.push(image);
        anchorImages.set(link, list);
      }
    });
    view.elements('a').forEach(node => {
      if (up(node, ['svg', 'math', 'noscript'])) return;
      const href = view.attr(node, 'href');
      if (href == null) return;
      const images = anchorImages.get(node) || [];
      facts.anchors.push({
        href: href.trim(),
        text: tidy(view.text(node)),
        label: tidy(view.attr(node, 'aria-label') || view.attr(node, 'title') || ''),
        imageAlt: tidy(images.map(image => image.alt || '').join(' '))
      });
    });
    // Scripts in document order. Merchant Custom Code sits between pairs of
    // wix/htmlEmbeds marker scripts; only that code is kept as text.
    let region = null;
    view.elements('script').forEach(node => {
      const type = lower(view.attr(node, 'type'));
      const id = view.attr(node, 'id') || '';
      const src = (view.attr(node, 'src') || '').trim();
      if (type === 'wix/htmlembeds') {
        const marker = /^pageHtmlEmbeds\.(head|bodyStart|bodyEnd) (start|end)$/.exec(id);
        if (marker) region = marker[2] === 'start' ? marker[1] : null;
        facts.scripts.push({ type, id, src, marker: true });
        return;
      }
      if (id === 'wix-viewer-model') { facts.modelText = view.text(node); facts.scripts.push({ type, id, src }); return; }
      if (id === 'wix-essential-viewer-model') { facts.essentialText = view.text(node); facts.scripts.push({ type, id, src }); return; }
      const entry = { type, id, src, region };
      if (type === 'application/ld+json') {
        entry.code = view.text(node);
        facts.ldjson.push({ text: entry.code, region });
      } else if (region && !src) {
        entry.code = view.text(node);
      } else if (!src && !type) {
        const code = view.text(node);
        if (code.length < 50000 && (code.indexOf('__ERROR_DATA__') !== -1 || code.indexOf('challenge-platform') !== -1 || code.indexOf('cf_chl_opt') !== -1)) facts.errorMarker = true;
      }
      facts.scripts.push(entry);
    });
    return facts;
  }

  // ---- Addresses and page types ----------------------------------------------

  const FREE_HOST = /\.(wixsite\.com|wixstudio\.com)$/i;
  const isFreeHost = host => FREE_HOST.test(host || '');
  const bareHost = host => String(host || '').toLowerCase().replace(/^www\./, '');

  function parseUrl(value, base) {
    if (!value) return null;
    try {
      const url = new URL(String(value).trim(), base);
      return url.protocol === 'http:' || url.protocol === 'https:' ? url : null;
    } catch (_) { return null; }
  }

  function pathParts(url) {
    let parts = url.pathname.split('/').filter(Boolean).map(part => {
      try { return decodeURIComponent(part).toLowerCase(); } catch (_) { return part.toLowerCase(); }
    });
    if (isFreeHost(url.hostname)) parts = parts.slice(1);
    return parts;
  }

  // Page type from the address, using Wix's default URL patterns (a free
  // address starts with the site's own folder, which is skipped). Pages that
  // don't need to be in search are flagged: cart, checkout, thank-you and
  // member pages, and blog tag and archive pages, which Wix doesn't index by
  // default. Product addresses can drop "product-page" in SEO Settings, so
  // og:type is the second clue (see analyzePage).
  function pageKind(url) {
    if (!url) return { type: 'unknown', special: null };
    const parts = pathParts(url);
    const first = parts[0] || '';
    let special = null;
    if (first === 'cart-page') special = 'cart';
    else if (first === 'checkout') special = 'checkout';
    else if (first === 'thank-you-page') special = 'thank-you';
    else if (first === 'members-area' || first === 'account') special = 'members';
    else if (first === 'blog' && parts[1] === 'tags') special = 'blog-tag';
    else if (first === 'blog' && parts[1] === 'archive') special = 'blog-archive';
    let type = 'page';
    if (!parts.length) type = 'home';
    else if (first === 'product-page') type = 'product';
    else if (first === 'category') type = 'category';
    else if (first === 'post' || first === 'single-post') type = 'blog-post';
    return { type, special };
  }

  function samePath(a, b) {
    const norm = url => {
      const parts = url.pathname.split('/').filter(Boolean).map(part => {
        try { return decodeURIComponent(part).toLowerCase(); } catch (_) { return part.toLowerCase(); }
      });
      return `/${parts.join('/')}`;
    };
    return norm(a) === norm(b);
  }

  const shortUrl = url => (url ? `${url.host}${url.pathname === '/' ? '' : url.pathname}` : '');

  // What the visitor typed in "Page address": an address, or why it isn't one.
  function readAddress(input) {
    let typed = String(input == null ? '' : input).trim();
    if (!typed) return { ok: true, url: null };
    typed = typed.replace(/^view-source:/i, '');
    if (!/^[a-z][a-z0-9+.-]*:/i.test(typed) && /^[^\s/]+\.[^\s/]+/.test(typed)) typed = `https://${typed}`;
    const url = parseUrl(typed);
    if (!url || !url.hostname.includes('.')) return { ok: false, error: 'not-url', message: 'This doesn’t look like a web address. Copy it from your browser’s address bar.' };
    const host = url.hostname.toLowerCase();
    if (/^(editor|manage)\.wix\.com$/.test(host) || (/(^|\.)wix\.com$/.test(host) && /^\/(dashboard|editor|my-account)/.test(url.pathname))) {
      return { ok: false, error: 'wix-editor', message: 'That’s a Wix dashboard or Editor address. Use your live page’s address, as visitors see it.' };
    }
    return { ok: true, url };
  }

  // ---- Text checks ------------------------------------------------------------

  const titleWidth = text => shared.approximateWidth(text, { size: 20 });

  // The longest start of the title that fits the width, with an ellipsis.
  function cutToWidth(text, max) {
    if (titleWidth(text) <= max) return text;
    const parts = shared.graphemes(text);
    const ellipsis = titleWidth('…');
    let width = 0;
    let fits = 0;
    for (let i = 0; i < parts.length; i += 1) {
      width += titleWidth(parts[i]);
      if (width + ellipsis > max) break;
      fits = i + 1;
    }
    if (fits >= parts.length) return text;
    return `${parts.slice(0, fits).join('').trimEnd()}…`;
  }

  // Sample text Wix templates ship with. Seen live on real stores (R10).
  const normalizeQuotes = value => String(value).replace(/[‘’ʼ]/g, "'").replace(/\s+/g, ' ').trim().toLowerCase();
  function templateText(value) {
    const text = normalizeQuotes(value);
    if (!text) return false;
    // A short name like "I'm a product" counts on its own or before " | site name".
    const name = text.split(' | ')[0].replace(/[.!]+$/, '');
    return text.includes("i'm a product description") || text.includes('this is your category description') || text.includes("i'm a paragraph") || text.includes('add paragraph text') || text.includes('lorem ipsum') ||
      name === "i'm a product" || name === "i'm a title" || name === 'add a title';
  }

  const IMAGE_EXT = /\.(png|jpe?g|gif|webp|avif|svg|heic|tiff?|bmp)$/i;
  const CAMERA_NAME = /^(img|dsc|dscn|dcim|pxl|mvimg|photo|image|screenshot|screen shot|whatsapp image|untitled|gemini_generated_image|chatgpt image)[\s_-]*[\d(]/i;
  const WIX_MEDIA_ID = /^[0-9a-f]{6}_[0-9a-f]{32}/i;
  function altIsFileName(alt) {
    const text = tidy(alt).replace(/^thumbnail:\s*/i, '');
    return IMAGE_EXT.test(text) || CAMERA_NAME.test(text) || WIX_MEDIA_ID.test(text);
  }

  // The file name at the end of a Wix media address, when it is a person's
  // file name rather than Wix's media id (product photos end in the id).
  function humanFileName(src) {
    if (!src || src.startsWith('data:')) return null;
    let last = '';
    try { last = decodeURIComponent(new URL(src, 'https://page.invalid/').pathname.split('/').pop() || ''); } catch (_) { return null; }
    if (!last || !IMAGE_EXT.test(last)) return null;
    if (WIX_MEDIA_ID.test(last) || /^[0-9a-f]{32}(~mv2)?\.[a-z0-9]+$/i.test(last) || /^file\.[a-z]+$/i.test(last)) return null;
    return last;
  }
  const cameraFileName = name => CAMERA_NAME.test(name) || /\(\d+\)\.[a-z]+$/i.test(name) || /^[\d_ -]+\.[a-z]+$/i.test(name);

  function isPixel(image) {
    const w = Number(image.width);
    const h = Number(image.height);
    if (image.width != null && image.height != null && w <= 1 && h <= 1) return true;
    if (/display\s*:\s*none/i.test(image.style)) return true;
    const host = hostOf(image.src);
    return Boolean(host) && PIXEL_HOSTS.test(host) && !/\.(png|jpe?g|gif|webp|avif|svg)(\?|$)/i.test(image.src.split('#')[0]);
  }

  // ---- Structured data ---------------------------------------------------------

  // Where a JSON block breaks, in plain words. JSON.parse error text differs
  // between browsers (Safari gives no position), so we find the spot ourselves.
  function jsonProblem(text) {
    let i = 0;
    const n = text.length;
    let lastComma = -1;
    const stop = (message, at = i) => { const error = new Error(message); error.at = Math.min(at, n); throw error; };
    const ws = () => { while (i < n && (text[i] === ' ' || text[i] === '\t' || text[i] === '\n' || text[i] === '\r')) i += 1; };
    const describe = char => (char === undefined ? 'end of the block' : `“${char}”`);
    function string() {
      i += 1;
      while (i < n) {
        const char = text[i];
        if (char === '"') { i += 1; return; }
        if (char === '\\') { i += 2; continue; }
        if (char === '\n' || char === '\r') stop('A line break inside quotes. Keep each value on one line');
        i += 1;
      }
      stop('A quote is never closed', n);
    }
    function number() {
      const start = i;
      if (text[i] === '-') i += 1;
      while (i < n && /[0-9.eE+-]/.test(text[i])) i += 1;
      if (!/^-?(0|[1-9][0-9]*)(\.[0-9]+)?([eE][+-]?[0-9]+)?$/.test(text.slice(start, i))) stop('A number isn’t written the way JSON needs', start);
    }
    function value() {
      ws();
      const char = text[i];
      if (char === '{') return object();
      if (char === '[') return array();
      if (char === '"') return string();
      if (char === '-' || (char >= '0' && char <= '9')) return number();
      if (text.startsWith('true', i)) { i += 4; return undefined; }
      if (text.startsWith('false', i)) { i += 5; return undefined; }
      if (text.startsWith('null', i)) { i += 4; return undefined; }
      if (char === "'") stop('Single quotes. JSON needs double quotes (")');
      if (char === '“' || char === '”') stop('Curly quotes. JSON needs straight double quotes (")');
      if (char === '/') stop('A comment. JSON can’t hold comments');
      if (char === undefined) stop('The block ends too early: something is missing at the end', n);
      return stop(`Unexpected ${describe(char)}`);
    }
    function object() {
      i += 1;
      ws();
      if (text[i] === '}') { i += 1; return; }
      for (;;) {
        ws();
        if (text[i] === '}') stop('An extra comma before }', lastComma);
        if (text[i] === "'") stop('Single quotes around a name. JSON needs double quotes (")');
        if (text[i] === '“' || text[i] === '”') stop('Curly quotes. JSON needs straight double quotes (")');
        if (text[i] !== '"') stop(text[i] === undefined ? 'The block ends too early: a } is missing' : `A name in double quotes should come here, not ${describe(text[i])}`);
        string();
        ws();
        if (text[i] !== ':') stop(`A colon (:) is missing after a name`);
        i += 1;
        value();
        ws();
        if (text[i] === ',') { lastComma = i; i += 1; continue; }
        if (text[i] === '}') { i += 1; return; }
        stop(text[i] === undefined ? 'The block ends too early: a } is missing' : 'A comma is missing between two values');
      }
    }
    function array() {
      i += 1;
      ws();
      if (text[i] === ']') { i += 1; return; }
      for (;;) {
        ws();
        if (text[i] === ']') stop('An extra comma before ]', lastComma);
        value();
        ws();
        if (text[i] === ',') { lastComma = i; i += 1; continue; }
        if (text[i] === ']') { i += 1; return; }
        stop(text[i] === undefined ? 'The block ends too early: a ] is missing' : 'A comma is missing between two values');
      }
    }
    try {
      value();
      ws();
      if (i < n) stop('Extra text after the end of the block');
      return null;
    } catch (error) {
      const at = typeof error.at === 'number' ? error.at : i;
      const before = text.slice(0, at);
      const line = before.split('\n').length;
      const column = at - before.lastIndexOf('\n');
      // Show the line around the problem: all of it when short, else about
      // 40 characters either side, starting at a word.
      const lineStart = before.lastIndexOf('\n') + 1;
      const lineEnd = text.indexOf('\n', at) === -1 ? n : text.indexOf('\n', at);
      let from = lineStart;
      if (at - lineStart > 64) {
        from = at - 40;
        const space = text.lastIndexOf(' ', from + 8);
        if (space > lineStart && space < at) from = space + 1;
      }
      const to = Math.min(lineEnd, at + 32);
      return {
        message: error.message,
        line,
        column,
        before: `${from > lineStart ? '…' : ''}${text.slice(from, at).replace(/^\s+/, '')}`,
        at: text.slice(at, at + 1),
        after: `${text.slice(at + 1, to).replace(/\s+$/, '')}${to < lineEnd ? '…' : ''}`
      };
    }
  }

  const typesOf = node => asArray(node && node['@type']).map(type => String(type));
  const hasType = (node, name) => typesOf(node).some(type => type.toLowerCase() === name.toLowerCase());
  // A @graph wrapper holds the @context for every node in it.
  const nodesOf = data => {
    const out = [];
    asArray(data).forEach(item => {
      if (!item || typeof item !== 'object') return;
      if (Array.isArray(item['@graph'])) {
        item['@graph'].forEach(inner => {
          if (!inner || typeof inner !== 'object') return;
          out.push(item['@context'] && !inner['@context'] ? Object.assign({ '@context': item['@context'] }, inner) : inner);
        });
      } else out.push(item);
    });
    return out;
  };

  // Does this block have the shape Wix writes itself? Those blocks carry
  // Wix's own oddities (prices as text, lowercase itemPage) and are never
  // reported as errors: the owner can only change them by converting the
  // preset to custom markup.
  function wixShaped(node) {
    const keys = Object.keys(node);
    const only = allowed => keys.every(key => allowed.indexOf(key) !== -1);
    const context = String(node['@context'] || '');
    if (!node['@type'] && Array.isArray(node.images)) return true;
    if (hasType(node, 'Product')) {
      const image = Array.isArray(node.image) ? node.image[0] : null;
      const offer = node.offers && !Array.isArray(node.offers) ? node.offers : null;
      return context === 'https://schema.org/' && Boolean(image && image['@type'] === 'ImageObject' && ('representativeOfPage' in image || 'thumbnail' in image)) && Boolean(offer && offer['@type'] === 'Offer');
    }
    if (hasType(node, 'ItemList')) return asArray(node.itemListElement).every(item => item && item['@type'] === 'ListItem' && item.item && hasType(item.item, 'Product'));
    if (hasType(node, 'BlogPosting')) return Boolean(node.mainEntityOfPage && node.mainEntityOfPage['@type'] === 'itemPage');
    if (hasType(node, 'WebSite')) return only(['@context', '@type', 'name', 'url', 'potentialAction']);
    if (hasType(node, 'LocalBusiness')) return only(['@context', '@type', 'name', 'url', 'image', 'address', 'telephone']);
    return false;
  }

  // Google's availability values for merchant listings, short or full.
  const AVAILABILITY = ['BackOrder', 'Discontinued', 'InStock', 'InStoreOnly', 'LimitedAvailability', 'OnlineOnly', 'OutOfStock', 'PreOrder', 'PreSale', 'SoldOut'];
  function availabilityName(value) {
    const text = String(value || '').trim();
    const short = text.replace(/^https?:\/\/schema\.org\//i, '');
    return AVAILABILITY.find(name => name === short) || null;
  }

  function priceText(offer) {
    const price = offer.price != null ? offer.price : offer.priceSpecification && offer.priceSpecification.price;
    return price == null ? '' : String(price).trim();
  }
  function currencyText(offer) {
    const currency = offer.priceCurrency || (offer.priceSpecification && offer.priceSpecification.priceCurrency);
    return currency == null ? '' : String(currency).trim();
  }

  // ---- Custom Code and outside scripts ----------------------------------------------

  // Code we recognise inside Custom Code snippets or script addresses. `app`
  // lists words an installed app's name holds when that app is the source,
  // for the "left over from a removed app" hint. `tracking` marks the tools
  // Wix names as not working through Custom Code.
  const VENDORS = [
    { key: 'meta-pixel', name: 'Meta Pixel (Facebook)', test: /fbq\(|connect\.facebook\.net|facebook\.com\/tr/i, tracking: true },
    { key: 'tiktok', name: 'TikTok Pixel', test: /ttq\.|analytics\.tiktok\.com/i, tracking: true },
    { key: 'gtm', name: 'Google Tag Manager', test: /googletagmanager\.com\/(gtm\.js|ns\.html)|GTM-[A-Z0-9]{4,}/, tracking: true },
    { key: 'google-analytics', name: 'Google Analytics', test: /gtag\/js\?id=G-|gtag\(\s*['"]config['"]\s*,\s*['"]G-|google-analytics\.com/i, tracking: true },
    { key: 'google-ads', name: 'Google Ads tag', test: /gtag\/js\?id=AW-|['"]AW-\d+/i },
    { key: 'json-ld', name: 'Structured data (JSON-LD)', test: /application\/ld\+json/i },
    { key: 'google-verification', name: 'Google site verification', test: /google-site-verification/i },
    { key: 'pinterest-verification', name: 'Pinterest site verification', test: /p:domain_verify/i },
    { key: 'facebook-verification', name: 'Facebook domain verification', test: /facebook-domain-verification/i },
    { key: 'pinterest-tag', name: 'Pinterest Tag', test: /pintrk\(|s\.pinimg\.com\/ct/i },
    { key: 'snap', name: 'Snap Pixel', test: /snaptr\(|sc-static\.net/i },
    { key: 'linkedin', name: 'LinkedIn Insight Tag', test: /snap\.licdn\.com|_linkedin_partner_id/i },
    { key: 'bing', name: 'Microsoft Advertising (UET)', test: /bat\.bing\.com|uetq/i },
    { key: 'x-pixel', name: 'X (Twitter) pixel', test: /static\.ads-twitter\.com|twq\(/i },
    { key: 'clarity', name: 'Microsoft Clarity', test: /clarity\.ms|\(c,l,a,r,i,t,y\)/i, app: ['microsoft clarity'] },
    { key: 'hotjar', name: 'Hotjar', test: /\bhotjar\b/i, app: ['hotjar'] },
    { key: 'klaviyo', name: 'Klaviyo', test: /\bklaviyo\b/i, app: ['klaviyo'] },
    { key: 'mailchimp', name: 'Mailchimp', test: /chimpstatic\.com|list-manage\.com|\bmailchimp\b/i, app: ['mailchimp'] },
    { key: 'omnisend', name: 'Omnisend', test: /omnisnippet|omnisrc|\bomnisend\b/i, app: ['omnisend'] },
    { key: 'poptin', name: 'Poptin', test: /\bpoptin\b|popt\.in/i, app: ['poptin'] },
    { key: 'privy', name: 'Privy', test: /privy\.com|widget\.privy/i, app: ['privy'] },
    { key: 'tidio', name: 'Tidio', test: /\btidio\b/i, app: ['tidio'] },
    { key: 'intercom', name: 'Intercom', test: /\bintercom\b|widget\.intercom\.io/i, app: ['intercom'] },
    { key: 'zendesk', name: 'Zendesk', test: /zdassets\.com|\bzendesk\b/i, app: ['zendesk'] },
    { key: 'trustpilot', name: 'Trustpilot', test: /\btrustpilot\b/i, app: ['trustpilot'] },
    { key: 'hubspot', name: 'HubSpot', test: /hs-scripts\.com|hs-analytics|\bhubspot\b/i, app: ['hubspot'] },
    { key: 'leadconnector', name: 'LeadConnector', test: /leadconnectorhq\.com/i },
    { key: 'wix-app-banner', name: 'Wix mobile app banner', test: /mobile-app-invite-banner/i, wix: true },
    // Made up for the problem sample (a reserved .example host): it can't occur on a real page.
    { key: 'fernwhistle', name: 'Fernwhistle Forms', test: /fernwhistle-forms\.example/i, app: ['fernwhistle'] }
  ];
  const VENDOR_BY_KEY = Object.fromEntries(VENDORS.map(vendor => [vendor.key, vendor]));

  function vendorsIn(code) {
    if (!code) return [];
    return VENDORS.filter(vendor => vendor.test.test(code));
  }

  // Hosts Wix serves its own code from. Everything else is "outside".
  const WIX_HOST = /(^|\.)(parastorage\.com|wixstatic\.com|wix\.com|wixapps\.net|wixsite\.com|wixstudio\.com|filesusr\.com|usrfiles\.com|wixmp\.com)$/i;
  const isWixHost = host => WIX_HOST.test(host) || host === 'browser.sentry-cdn.com';
  const HOST_NAMES = [
    [/(^|\.)googletagmanager\.com$/, 'Google Tag Manager or Google tag'],
    [/(^|\.)google-analytics\.com$/, 'Google Analytics'],
    [/(^|\.)connect\.facebook\.net$/, 'Meta Pixel (Facebook)'],
    [/(^|\.)analytics\.tiktok\.com$/, 'TikTok Pixel'],
    [/(^|\.)hotjar\.com$/, 'Hotjar'],
    [/(^|\.)clarity\.ms$/, 'Microsoft Clarity'],
    [/(^|\.)klaviyo\.com$/, 'Klaviyo'],
    [/(^|\.)chimpstatic\.com$/, 'Mailchimp'],
    [/(^|\.)(omnisnippet1\.com|omnisrc\.com|omnisend\.com)$/, 'Omnisend'],
    [/(^|\.)(popt\.in|poptin\.com)$/, 'Poptin'],
    [/(^|\.)tidio\.co$/, 'Tidio'],
    [/(^|\.)(intercom\.io|intercomcdn\.com)$/, 'Intercom'],
    [/(^|\.)zdassets\.com$/, 'Zendesk'],
    [/(^|\.)trustpilot\.com$/, 'Trustpilot'],
    [/(^|\.)leadconnectorhq\.com$/, 'LeadConnector'],
    [/(^|\.)(hs-scripts\.com|hs-analytics\.net)$/, 'HubSpot'],
    [/(^|\.)licdn\.com$/, 'LinkedIn Insight Tag'],
    [/(^|\.)pinimg\.com$/, 'Pinterest Tag'],
    [/(^|\.)bat\.bing\.com$/, 'Microsoft Advertising (UET)'],
    [/(^|\.)sc-static\.net$/, 'Snap Pixel'],
    [/(^|\.)ads-twitter\.com$/, 'X (Twitter) pixel'],
    [/(^|\.)privy\.com$/, 'Privy'],
    [/(^|\.)jsdelivr\.net$/, 'jsDelivr, a public code host'],
    [/(^|\.)cdnjs\.cloudflare\.com$/, 'cdnjs, a public code host'],
    [/(^|\.)unpkg\.com$/, 'unpkg, a public code host'],
    [/(^|\.)fernwhistle-forms\.example$/, 'Fernwhistle Forms']
  ];
  const hostName = host => (HOST_NAMES.find(([pattern]) => pattern.test(host)) || [null, null])[1];
  const POSITION_WORDS = { head: 'Head', bodyStart: 'Body – start', bodyEnd: 'Body – end' };

  // ---- Wix click paths (exact labels from Wix Help, 2026) ------------------------------

  const EDITOR_PATHS = {
    editor: ['Pages & Menu', 'More Actions', 'SEO basics'],
    studio: ['Pages', 'More Actions', 'SEO Basics'],
    harmony: ['Page', 'More Actions', 'SEO and accessibility', 'SEO']
  };
  const EDITOR_NAMES = { editor: 'Wix Editor', studio: 'Studio Editor', harmony: 'Wix Harmony' };
  const PRODUCT_SEO = tab => ['Products', '(this product)', 'Edit SEO Settings', tab];
  const CATEGORY_SEO = ['Categories', '(this category)', 'Edit SEO Settings'];
  const PATTERN = type => ['SEO & GEO', 'Tools and settings', 'SEO Settings', type, 'Customize defaults'];
  const SITE_SEO = ['SEO & GEO', 'Tools and settings', 'SEO Settings'];
  const CUSTOM_CODE = ['Settings', 'Custom Code'];
  const PATTERN_TYPES = { product: 'Products', category: 'Categories', 'blog-post': 'Blog Posts', home: 'Main Pages', page: 'Main Pages', unknown: 'the page type' };

  function editorOrder(meta) {
    return meta.editor === 'studio' ? ['studio', 'editor', 'harmony'] : ['editor', 'studio', 'harmony'];
  }

  // The page's own SEO settings for this page type, editor routes first.
  // Products, categories and blog posts are edited in the dashboard. Wix
  // documents no Harmony editor path to the Advanced or Social share
  // settings, so Harmony uses the dashboard's Edit by Page route, which works
  // for every editor. `harmonyThen` replaces `then` on the Harmony step.
  function pageSettingSteps(meta, then, tab, harmonyThen) {
    if (meta.pageType === 'product') return [{ who: 'This product', path: PRODUCT_SEO(tab || 'Basics'), then }];
    if (meta.pageType === 'category') return [{ who: 'This category', path: CATEGORY_SEO.concat(tab === 'Advanced' ? ['Advanced'] : []), then }];
    if (meta.pageType === 'blog-post') {
      if (tab === 'Social share') return [{ who: 'Every blog post', path: PATTERN('Blog Posts'), then: ['click Edit next to ', { q: 'Basics & social share' }, ' and scroll down to Social share.'] }];
      return [{ who: 'This blog post', path: ['Blog', '(this post)', 'SEO'].concat(tab === 'Advanced' ? ['Advanced'] : []), then }];
    }
    return editorOrder(meta).map(key => {
      if (key === 'harmony' && tab) {
        return { who: EDITOR_NAMES.harmony, path: ['SEO & GEO', 'Tools and settings', 'SEO Settings', 'Main Pages', 'Edit by Page', '(this page)', tab === 'Advanced' ? 'Advanced' : 'Social share'], then };
      }
      const path = EDITOR_PATHS[key].slice();
      if (tab === 'Advanced') path.push('Advanced SEO');
      if (tab === 'Social share') path.push('Social share');
      return { who: EDITOR_NAMES[key], path, then: key === 'harmony' && harmonyThen ? harmonyThen : then };
    });
  }

  const HEADING_STEPS = meta => {
    const steps = {
      editor: { who: 'Wix Editor', path: ['Edit Text', 'SEO & accessibility', 'Choose HTML tag'] },
      studio: { who: 'Studio Editor', path: ['Edit Text', 'Inspector', 'HTML tag'] },
      harmony: { who: 'Wix Harmony', text: ['Use the ', { b: 'Text styles' }, ' drop-down on the action bar.'] }
    };
    return editorOrder(meta).map(key => steps[key]).concat([{ who: 'Text inside a Wix app, like Wix Stores', path: ['Settings', 'SEO & accessibility', 'Change heading tag'] }]);
  };

  // ---- The checks ----------------------------------------------------------------

  function metaContent(facts, test) {
    return facts.metas.filter(test).map(meta => (meta.content == null ? '' : meta.content));
  }

  function tagMarkup(meta) {
    const key = meta.property ? 'property' : 'name';
    return `<meta ${key}="${meta.property || meta.name}" content="${meta.content == null ? '' : meta.content}">`;
  }

  function finding(group, id, status, title, extra = {}) {
    return Object.assign({ id, group, status, title, found: [], why: [], fix: [], links: [], sourceKeys: [] }, extra);
  }

  function readModel(text) {
    if (!text) return null;
    const data = readJson(text);
    if (!data || typeof data !== 'object') return { broken: true };
    const site = data.site || {};
    const zoom = (data.siteFeaturesConfigs && data.siteFeaturesConfigs.accessibilityBrowserZoom) || {};
    return {
      broken: false,
      editorName: String(site.editorName || ''),
      studio: site.editorName === 'Studio' || zoom.isStudio === true,
      requestUrl: typeof data.requestUrl === 'string' ? data.requestUrl : '',
      baseUrl: typeof site.externalBaseUrl === 'string' ? site.externalBaseUrl : '',
      multilingual: Boolean(data.language && data.language.isMultilingualEnabled)
    };
  }

  function checkIndexing(ctx) {
    const { facts, meta, add } = ctx;
    const robots = facts.metas.filter(item => item.name === 'robots' || item.name === 'googlebot');
    const directives = new Set();
    robots.forEach(item => String(item.content || '').split(',').forEach(part => { const word = lower(part); if (word) directives.add(word.replace(/\s+/g, '')); }));
    const found = robots.map(item => [{ code: tagMarkup(item) }]);
    const special = meta.special;
    const specialWord = { cart: 'cart', checkout: 'checkout', 'thank-you': 'thank-you', members: 'member', 'blog-tag': 'blog tag', 'blog-archive': 'blog archive' }[special];
    const noindex = directives.has('noindex') || directives.has('none');
    const nofollow = directives.has('nofollow') || directives.has('none');
    meta.noindex = noindex;
    if (noindex && special) {
      const blogList = special === 'blog-tag' || special === 'blog-archive';
      add(finding('index', 'robots-noindex', 'info', `This ${specialWord} page tells Google not to index it. That’s normal.`, {
        found,
        why: [blogList
          ? ['Wix keeps blog tag and archive pages out of search by default. There’s nothing to change.']
          : special === 'members'
            ? ['Member pages are often kept out of search, so a noindex here is usually fine.']
            : ['Cart, checkout and thank-you pages don’t need to be in search, so a noindex here is fine.']],
        sourceKeys: blogList || special === 'members' ? ['wix-defaults'] : []
      }));
    } else if (noindex) {
      const typeName = PATTERN_TYPES[meta.pageType] || 'the page type';
      add(finding('index', 'robots-noindex', 'fix', 'This page tells Google not to index it.', {
        found,
        why: [['noindex asks search engines to leave a page out of their results. While it’s here, this page won’t show in Google. If you hid it on purpose, leave it.']],
        fix: pageSettingSteps(meta, ['turn on ', { q: 'Let search engines index this page' }, '.'], null, ['click ', { q: 'Let search engines index this page' }, ', then Edit.']).concat([
          { text: ['Still off? Check the default for every page of this type: ', { path: PATTERN(typeName) }, ', then Edit next to ', { q: 'Basics & social share' }, ': ', { q: 'Let search engines index your page(s)' }, '.'] },
          { text: ['Then the switch for the whole site: ', { path: SITE_SEO }, ', ', { q: 'Let search engines index your site' }, '. When it’s off, Wix hides every page.'] },
          { text: ['A noindex can also be added under ', { q: 'Robots Meta Tag' }, ' in the page’s advanced SEO settings.'] }
        ]),
        links: [GUIDES.indexing],
        sourceKeys: ['wix-noindex', 'wix-index-site', 'wix-robots', 'google-indexing']
      }));
    }
    if (nofollow && !directives.has('none')) {
      add(finding('index', 'robots-nofollow', special ? 'info' : 'check', 'This page tells Google not to follow its links.', {
        found,
        why: [['nofollow asks search engines not to follow the links on this page. Check that you meant to add it.']],
        fix: special ? [] : pageSettingSteps(meta, ['look under ', { q: 'Robots Meta Tag' }, '.'], 'Advanced'),
        sourceKeys: ['wix-robots']
      }));
    }
    const others = Array.from(directives).filter(word => !['index', 'follow', 'noindex', 'nofollow', 'none', 'all'].includes(word));
    if (others.length) {
      add(finding('index', 'robots-other', 'info', `This page has extra robots rules: ${others.join(', ')}.`, {
        found,
        why: [['These change how Google shows the page, not whether it can be found. Wix sets them under ', { b: 'Advanced SEO' }, ' › ', { q: 'Robots Meta Tag' }, '.']],
        sourceKeys: ['wix-robots']
      }));
    }
    if (!noindex) {
      add(finding('index', 'robots-ok', 'fine', 'Nothing here tells Google to skip this page.', {
        found: found.length ? found : [['No robots tag, so the page doesn’t ask to be left out.']]
      }));
    }

    // Canonical tags, compared with the address the source itself came from
    // (a typed address only stands in when the source has none).
    const canonicals = facts.links.filter(link => link.rel.includes('canonical'));
    const pageUrl = meta.checkUrl;
    const canonicalUrls = canonicals.map(link => parseUrl(link.href, pageUrl ? pageUrl.href : undefined));
    if (!canonicals.length) {
      add(finding('index', 'canonical-missing', 'check', 'This page has no canonical tag.', {
        partial: 'a canonical tag',
        why: [['A canonical tag names the main address of a page, so search engines know which copy to show.', meta.isWix ? ' Wix adds one to every page by default, so a missing one is unusual.' : '']],
        fix: pageSettingSteps(meta, ['look under ', { q: 'Additional Tags' }, ' for a changed or removed canonical. Wix suggests resetting a customized canonical when a page isn’t showing in search.'], 'Advanced'),
        sourceKeys: ['wix-canonical', 'wix-not-found']
      }));
    } else if (canonicals.length > 1) {
      const distinct = new Set(canonicals.map(link => link.href));
      add(finding('index', 'canonical-multiple', distinct.size > 1 ? 'fix' : 'check', distinct.size > 1 ? `This page has ${countWord(canonicals.length).toLowerCase()} canonical tags that name different addresses.` : 'This page has the same canonical tag twice.', {
        found: canonicals.map(link => [{ code: `<link rel="canonical" href="${link.href}">` }]),
        why: [[meta.isWix ? 'Wix writes one canonical tag itself, so the other came from somewhere else, such as Custom Code or an app.' : 'A page should have one canonical tag.', distinct.size > 1 ? ' With two different answers, search engines pick one themselves.' : '']],
        fix: [
          { text: ['Look for a canonical tag in ', { path: CUSTOM_CODE }, ', in the Development & integrations section.'] },
          { text: ['Also check the page’s advanced SEO settings, under ', { q: 'Additional Tags' }, '. Keep one.'] }
        ],
        sourceKeys: ['wix-canonical', 'wix-custom-code']
      }));
    }
    if (canonicals.length === 1 || (canonicals.length > 1 && new Set(canonicals.map(link => link.href)).size === 1)) {
      const canonical = canonicalUrls[0];
      const resetSteps = pageSettingSteps(meta, ['open ', { q: 'Additional Tags' }, ', find canonical and reset it to Wix’s default.'], 'Advanced');
      if (!canonical) {
        add(finding('index', 'canonical-broken', 'check', 'The canonical tag doesn’t hold a web address.', {
          found: [[{ code: `<link rel="canonical" href="${canonicals[0].href}">` }]],
          fix: resetSteps,
          sourceKeys: ['wix-canonical']
        }));
      } else {
        let problem = false;
        const pageFound = pageUrl ? [['This page: ', { code: shortUrl(pageUrl) }]] : [];
        if (canonical.protocol === 'http:') {
          problem = true;
          add(finding('index', 'canonical-http', 'check', 'The canonical tag starts with http://, not https://.', {
            found: [[{ code: canonical.href }]],
            why: [['All Wix sites use HTTPS, Wix says. An http:// canonical points search engines at an address that only redirects.']],
            fix: resetSteps,
            sourceKeys: ['wix-https', 'wix-canonical']
          }));
        }
        if (pageUrl && bareHost(canonical.hostname) !== bareHost(pageUrl.hostname)) {
          problem = true;
          if (isFreeHost(canonical.hostname) && !isFreeHost(pageUrl.hostname)) {
            add(finding('index', 'canonical-free-address', 'fix', 'The canonical tag points to your free Wix address.', {
              found: [['Canonical: ', { code: shortUrl(canonical) }]].concat(pageFound),
              why: [['A canonical tag names the main address of a page. Once a domain is connected, Wix sends the free address to your domain, and Wix says search engines can no longer find the free address. This tag points them at it anyway.']],
              fix: [{ text: ['Wix’s own canonical names the page’s own address, so this one was probably typed in by hand.'] }].concat(resetSteps),
              links: [GUIDES.indexing],
              sourceKeys: ['wix-free-url', 'wix-canonical', 'google-canonical']
            }));
          } else if (!isFreeHost(pageUrl.hostname)) {
            add(finding('index', 'canonical-other-host', 'check', 'The canonical tag names a page on another website.', {
              found: [['Canonical: ', { code: shortUrl(canonical) }]].concat(pageFound),
              why: [['A canonical tag tells search engines which address is the main copy. This one names another site, so they may show that one instead. Google treats the tag as a hint, not a rule.']],
              fix: [{ text: ['If you didn’t mean it, reset it.'] }].concat(resetSteps),
              sourceKeys: ['google-canonical', 'wix-canonical']
            }));
          }
        } else if (pageUrl && !samePath(canonical, pageUrl)) {
          problem = true;
          add(finding('index', 'canonical-other-page', 'check', 'The canonical tag names a different page as the main copy.', {
            found: [['Canonical: ', { code: shortUrl(canonical) }]].concat(pageFound),
            why: [['A canonical tag tells search engines which address is the main copy, and Google may show that page instead of this one.', meta.isWix ? ' Wix’s own canonical names the page itself, so this one was probably changed from Wix’s default.' : '']],
            fix: [{ text: ['If the two pages really are copies, leave it. If not, reset it:'] }].concat(resetSteps).concat([{ text: ['If this page has moved for good, a 301 redirect is the usual tool.'] }]),
            links: [GUIDES.redirects],
            sourceKeys: ['google-canonical', 'wix-canonical']
          }));
        }
        if (!problem) {
          add(finding('index', 'canonical-ok', 'fine', pageUrl ? 'The canonical tag names this page as the main copy.' : 'The page has one canonical tag.', {
            found: [[{ code: shortUrl(canonical) }]],
            why: [['A canonical tag names the main address of a page, so search engines know which copy to show.', meta.isWix ? ' Wix writes it for you.' : '']],
            sourceKeys: ['wix-canonical']
          }));
        }
        if (isFreeHost(canonical.hostname) && (!pageUrl || isFreeHost(pageUrl.hostname))) {
          add(finding('index', 'free-address', 'info', 'This page is on Wix’s free address.', {
            found: [[{ code: canonical.hostname }]],
            why: [['Free addresses look like ', { code: 'name.wixsite.com/site' }, ' or ', { code: 'name.wixstudio.com/site' }, '. Connecting your own domain gives your pages your own address; after that, Wix sends the free address to your domain. Connecting Google Search Console through Wix also needs a Premium plan and a connected domain.']],
            sourceKeys: ['wix-free-url', 'wix-gsc']
          }));
        }
      }
    }

    // Language.
    const lang = (facts.lang || '').trim();
    if (!lang) {
      add(finding('index', 'lang-missing', 'check', 'The page doesn’t name its language.', {
        why: [['The ', { code: 'lang' }, ' attribute on ', { code: '<html>' }, ' tells browsers and screen readers what language the page is in.', meta.isWix ? ' Wix usually sets it from your site language.' : '']],
        fix: [{ text: ['Check the site language in your dashboard under ', { q: 'Language & Region' }, '.'] }],
        sourceKeys: ['wix-language']
      }));
    } else {
      add(finding('index', 'lang-ok', 'fine', `The page names its language: ${languageName(lang)}.`, { found: [[{ code: `<html lang="${lang}">` }]] }));
    }

    // Language versions.
    const alternates = facts.links.filter(link => link.rel.includes('alternate') && link.hreflang);
    if (alternates.length) {
      const self = meta.checkUrl || meta.canonicalUrl;
      const listsSelf = self ? alternates.some(link => { const url = parseUrl(link.href, self.href); return url && bareHost(url.hostname) === bareHost(self.hostname) && samePath(url, self); }) : true;
      const hasDefault = alternates.some(link => lower(link.hreflang) === 'x-default');
      add(finding('index', 'hreflang', listsSelf ? 'info' : 'check', listsSelf ? `This page lists ${plural(alternates.length, 'language version')}.` : 'This page isn’t in its own list of language versions.', {
        found: alternates.slice(0, 8).map(link => { const url = parseUrl(link.href, self ? self.href : undefined); return [{ code: link.hreflang }, ' → ', { code: clip(url ? shortUrl(url) : link.href, 70) }]; }).concat(hasDefault ? [] : [['No x-default entry.']]),
        why: [['hreflang tags point search engines to the same page in other languages.', listsSelf ? '' : ' Google’s guide to language versions explains what each version should list.']],
        sourceKeys: ['google-hreflang']
      }));
    }
  }

  function languageName(code) {
    try {
      if (typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function') {
        const name = new Intl.DisplayNames(['en'], { type: 'language' }).of(code);
        if (name && name.toLowerCase() !== code.toLowerCase()) return `${name} (${code})`;
      }
    } catch (_) { /* fall back to the code */ }
    return code;
  }

  function checkSearch(ctx) {
    const { facts, meta } = ctx;
    // Cart, checkout, thank-you and member pages don't need to be in search,
    // nor do blog tag and archive pages while they're noindexed, so how they
    // would look in a result is only worth a note.
    const keptOut = meta.special && (meta.noindex || !/^blog-/.test(meta.special));
    const add = item => ctx.add(keptOut && item.status === 'check' ? Object.assign(item, { status: 'info' }) : item);
    const titles = facts.titles;
    const title = titles[0] || '';
    const siteName = tidy(metaContent(facts, item => item.property === 'og:site_name')[0] || '');
    const isProductish = meta.pageType === 'product' || meta.pageType === 'category';
    const titleSteps = then => pageSettingSteps(meta, then);
    if (!title) {
      add(finding('search', 'title-missing', 'fix', 'This page has no title tag.', {
        partial: 'a title tag',
        why: [['Wix calls the title tag the “title in search results”. This page has none, so search engines pick their own words.']],
        fix: titleSteps(['fill in ', { q: 'Title tag' }, '.']),
        links: [GUIDES.titles],
        sourceKeys: ['wix-seo-panel', 'google-title']
      }));
    } else {
      if (titles.length > 1) {
        add(finding('search', 'title-multiple', 'check', `This page has ${countWord(titles.length).toLowerCase()} title tags.`, {
          found: titles.map(item => [{ q: clip(item) }]),
          why: [['Browsers show the first one.', meta.isWix ? ' Wix writes one title itself, so the other came from somewhere else, such as Custom Code or an app.' : '']],
          fix: [{ text: ['Look in ', { path: CUSTOM_CODE }, ' (Development & integrations) for a ', { code: '<title>' }, ' tag and remove it.'] }],
          sourceKeys: ['wix-custom-code']
        }));
      }
      const pattern = siteName && title.length > siteName.length + 3 && title.endsWith(` | ${siteName}`);
      const pageName = pattern ? title.slice(0, -(siteName.length + 3)).trim() : title;
      const generic = /^(home|untitled|new page|blank|blank page|page)$/i.test(pageName) || /^copy of /i.test(pageName);
      if (generic) {
        const home = meta.isWix && meta.pageType === 'home' && /^home$/i.test(pageName);
        add(finding('search', 'title-default', 'check', home ? 'The title is Wix’s default home title.' : 'The title starts with a placeholder page name.', {
          found: [[{ q: title }]],
          why: [home
            ? ['Wix builds titles from the page name and site name; its own example is “Home | My Stunning Website”. Searchers see that in results. Wix’s own advice for a home page title: use at least one of your keywords and your business name.']
            : [meta.isWix ? 'Wix’s default title starts with the page’s name from your site menu, and this one looks like a placeholder. Say what the page is about.' : 'This title looks like a placeholder. Say what the page is about.']],
          fix: titleSteps(['write your own ', { q: 'Title tag' }, '.']),
          links: [GUIDES.titles],
          sourceKeys: home ? ['wix-home-title', 'wix-seo-panel'] : ['wix-defaults', 'wix-seo-panel']
        }));
      } else if (siteName && title === siteName) {
        add(finding('search', 'title-site-name', 'check', 'The title is only your site name.', {
          found: [[{ q: title }]],
          why: [meta.pageType === 'home'
            ? ['Searchers see this title in results. Wix’s own advice for a home page title: use at least one of your keywords and your business name.']
            : ['Searchers see this title in results, and it doesn’t say what this page is about.']],
          fix: titleSteps(['write your own ', { q: 'Title tag' }, '.']),
          links: [GUIDES.titles],
          sourceKeys: meta.pageType === 'home' ? ['wix-home-title'] : ['wix-titles']
        }));
      }
      // Two yardsticks: our width estimate for Google, and Wix's own advice
      // (under 60 characters; at most 60 for store products and categories).
      const chars = graphemeCount(title);
      const width = Math.round(titleWidth(title));
      const advice = !meta.isWix ? '' : isProductish ? 'Wix Stores recommends product and category titles of 60 characters at most. ' : 'Wix advises keeping titles under 60 characters. ';
      const google = 'Google cuts titles to fit the screen and may rewrite them; it doesn’t publish a limit.';
      const overWix = meta.isWix && (isProductish ? chars > 60 : chars >= 60);
      const titleKeys = meta.isWix ? [isProductish ? 'wix-product-seo' : 'wix-titles', 'google-title'] : ['google-title'];
      const shortenSteps = titleSteps(['shorten the ', { q: 'Title tag' }, '.']).concat(pattern && meta.isWix ? [{ text: ['Titles end with your site name by default. To change that for every page of this type: ', { path: PATTERN(PATTERN_TYPES[meta.pageType] || 'the page type') }, ', then Edit next to ', { q: 'Basics & social share' }, '.'] }] : []);
      if (width > TITLE_PX) {
        add(finding('search', 'title-length', 'check', `The title is ${chars} characters, so Google may cut it.`, {
          found: [[{ q: title }], [`${chars} characters · about ${width} px wide (our estimate)`], ['May show as: ', { q: cutToWidth(title, TITLE_PX) }]],
          why: [[advice, google, ' We estimate about 600 pixels of title show on a computer, measured in Arial at 20 px.']],
          fix: shortenSteps,
          links: [GUIDES.titles, GUIDES.patternTool],
          sourceKeys: titleKeys
        }));
      } else if (overWix) {
        add(finding('search', 'title-length', 'check', isProductish ? `The title is ${chars} characters, over Wix’s advice of 60.` : `The title is ${chars} characters; Wix advises under 60.`, {
          found: [[{ q: title }], [`${chars} characters · about ${width} px wide (our estimate)`]],
          why: [[advice, google, ' At about ', String(width), ' pixels it probably fits on a computer (our estimate), so this is Wix’s advice rather than a cut.']],
          fix: shortenSteps,
          links: [GUIDES.titles, GUIDES.patternTool],
          sourceKeys: titleKeys
        }));
      } else {
        add(finding('search', 'title-length', 'fine', `The title fits: ${chars} characters, about ${width} px wide.`, {
          found: [[{ q: title }]],
          why: [[advice, google]],
          sourceKeys: titleKeys
        }));
      }
      if (pattern && meta.isWix) {
        add(finding('search', 'title-pattern', 'info', 'The title ends with your site name, Wix’s default pattern.', {
          found: [[{ q: ` | ${siteName}` }, ` takes ${graphemeCount(siteName) + 3} of the ${chars} characters.`]],
          why: [['Wix builds titles as “page name | site name” unless you change the pattern. That’s fine; it leaves less room for the page’s own words.']],
          fix: [{ text: ['To change it for every page of this type: ', { path: PATTERN(PATTERN_TYPES[meta.pageType] || 'the page type') }, ', then Edit next to ', { q: 'Basics & social share' }, '.'] }],
          links: [GUIDES.patternTool],
          sourceKeys: ['wix-defaults', 'wix-patterns']
        }));
      }
    }

    const descriptions = metaContent(facts, item => item.name === 'description').map(tidy);
    const ogDescription = tidy(metaContent(facts, item => item.property === 'og:description')[0] || '');
    const description = descriptions[0] || '';
    const descriptionSteps = meta.pageType === 'product'
      ? [{ who: 'This product', path: PRODUCT_SEO('Basics'), then: ['fill in ', { q: 'Meta description' }, '.'] },
          { who: 'Every product at once', path: PATTERN('Products'), then: ['click Edit next to ', { q: 'Basics & social share' }, ' and add a variable such as Product Description.'] }]
      : pageSettingSteps(meta, ['fill in ', { q: 'Meta description' }, '.']).concat([{ who: 'Every page of this type', path: PATTERN(PATTERN_TYPES[meta.pageType] || 'the page type'), then: ['click Edit next to ', { q: 'Basics & social share' }, '.'] }]);
    const lengthAdvice = meta.isWix && (meta.pageType === 'product' || meta.pageType === 'category') ? 'Wix suggests 50–160 characters, and a maximum of 156 for products and categories.' : 'Wix suggests 50–160 characters.';
    if (!description) {
      add(finding('search', 'description-missing', 'check', 'This page has no meta description.', {
        partial: 'a meta description',
        found: [ogDescription ? ['Social previews use: ', { q: clip(ogDescription, 150) }] : ['There’s no social description either.']],
        why: [['Google will write its own snippet from the page. That is allowed. Add a description only if you want to control it.']].concat(meta.isWix ? [['Wix leaves the meta description blank by default on most page types.']] : []),
        fix: descriptionSteps,
        links: [GUIDES.titles],
        sourceKeys: ['wix-defaults', 'wix-seo', 'google-snippet']
      }));
    } else {
      if (descriptions.length > 1) {
        add(finding('search', 'description-multiple', 'check', `This page has ${countWord(descriptions.length).toLowerCase()} meta descriptions.`, {
          found: descriptions.map(item => [{ q: clip(item) }]),
          why: [[meta.isWix ? 'Wix writes one, so the other came from somewhere else, such as Custom Code or an app.' : 'Search engines use one; with two, they pick for themselves.']],
          fix: [{ text: ['Look in ', { path: CUSTOM_CODE }, ' for a description tag and remove it.'] }],
          sourceKeys: ['wix-custom-code']
        }));
      }
      const chars = graphemeCount(description);
      const limit = meta.pageType === 'product' || meta.pageType === 'category' ? 156 : 160;
      if (title && lower(description) === lower(title)) {
        add(finding('search', 'description-same-as-title', 'check', 'The meta description repeats the title.', {
          found: [[{ q: description }]],
          why: [['The description is the text under the title in Google. Repeating the title adds nothing a searcher can use.']],
          fix: descriptionSteps,
          sourceKeys: ['wix-titles']
        }));
      } else if (chars < 50) {
        add(finding('search', 'description-short', 'check', `The meta description is only ${chars} characters.`, {
          found: [[{ q: description }]],
          why: [[lengthAdvice, ' Google may write its own snippet from the page instead.']],
          fix: descriptionSteps,
          links: [GUIDES.titles],
          sourceKeys: ['wix-titles', 'google-snippet']
        }));
      } else if (chars > limit) {
        add(finding('search', 'description-long', 'check', `The meta description is ${chars} characters, so Google may cut it.`, {
          found: [[{ q: clip(description, 220) }]],
          why: [[lengthAdvice, ' Wix says Google shows about 160 characters on a computer and about 125 on a phone. Google says it cuts snippets to fit the screen.']],
          fix: descriptionSteps,
          links: [GUIDES.titles, GUIDES.patternTool],
          sourceKeys: ['wix-titles', 'wix-meta-hub', 'google-snippet']
        }));
      } else {
        add(finding('search', 'description-ok', 'fine', `The meta description is ${chars} characters.`, {
          found: [[{ q: description }]],
          why: [[lengthAdvice]],
          sourceKeys: ['wix-titles']
        }));
      }
    }

    // Social tags.
    const og = name => metaContent(facts, item => item.property === name)[0];
    const twitterCard = metaContent(facts, item => item.name === 'twitter:card')[0];
    const missing = ['og:title', 'og:description', 'og:image'].filter(name => !og(name)).concat(twitterCard ? [] : ['twitter:card']);
    const imageMissing = missing.includes('og:image');
    if (imageMissing) {
      const important = meta.pageType === 'home' || meta.pageType === 'product';
      add(finding('search', 'social-image-missing', important ? 'check' : 'info', 'This page has no social image (og:image).', {
        partial: 'a social image (og:image)',
        why: [['og:image is the picture apps like Facebook and Pinterest show when someone shares a link to the page. Without it, they may show none or pick their own.']],
        fix: (meta.pageType === 'product'
          ? [{ who: 'This product', path: PRODUCT_SEO('Social share'), then: ['add an image.'] }]
          : pageSettingSteps(meta, ['add an image.'], 'Social share')).concat([{ text: ['A default for every page: ', { q: 'General social image' }, ' in ', { q: 'Website Settings' }, ' in your dashboard.'] }]),
        sourceKeys: ['wix-social', 'wix-website-settings']
      }));
    }
    const otherMissing = missing.filter(name => name !== 'og:image');
    if (otherMissing.length) {
      add(finding('search', 'social-missing', 'info', `Some social tags are missing: ${otherMissing.join(', ')}.`, {
        partial: otherMissing.join(', '),
        why: [['Social tags set the title, text and picture apps show when someone shares a link to the page.']],
        fix: meta.pageType === 'product' ? [{ who: 'This product', path: PRODUCT_SEO('Social share') }] : pageSettingSteps(meta, null, 'Social share'),
        sourceKeys: ['wix-social']
      }));
    }
    if (!missing.length) {
      add(finding('search', 'social-ok', 'fine', 'Social tags are set: og:title, og:description, og:image and twitter:card.', {
        why: [['Social tags set the title, text and picture apps show when someone shares a link to the page.']],
        sourceKeys: ['wix-social']
      }));
    }
  }

  function checkPage(ctx) {
    const { facts, meta, add, blocks } = ctx;
    // Template text left on the live page.
    const places = [];
    const look = (where, value) => { if (value && templateText(value)) places.push([where, tidy(value)]); };
    look('the title', facts.titles[0]);
    metaContent(facts, item => item.name === 'description').forEach(value => look('the meta description', value));
    metaContent(facts, item => item.property === 'og:title').forEach(value => look('the social title (og:title)', value));
    const ogText = metaContent(facts, item => item.property === 'og:description');
    ogText.forEach(value => look('the social description (og:description)', value));
    // X reuses the og text on Wix pages; name it only when it differs.
    metaContent(facts, item => item.name === 'twitter:description').forEach(value => { if (!ogText.some(other => tidy(other) === tidy(value))) look('the X (Twitter) description', value); });
    facts.headings.forEach(heading => look(`a heading (H${heading.level})`, heading.text));
    facts.paragraphs.forEach(text => look('a paragraph', text));
    facts.images.forEach(image => look('a picture’s alt text', image.alt));
    blocks.forEach(block => block.nodes.forEach(node => {
      const type = typesOf(node)[0] || 'block';
      if (typeof node.name === 'string') look(`the structured data (${type} name)`, node.name);
      if (typeof node.description === 'string') look(`the structured data (${type} description)`, node.description);
    }));
    const uniquePlaces = [];
    places.forEach(([where, value]) => { if (!uniquePlaces.some(item => item[0] === where)) uniquePlaces.push([where, value]); });
    if (uniquePlaces.length) {
      const one = uniquePlaces.length === 1;
      add(finding('page', 'template-text', 'fix', one ? `This page still has template text in ${uniquePlaces[0][0].replace(/^the /, 'its ')}.` : `This page still has template text in ${countWord(uniquePlaces.length).toLowerCase()} places.`, {
        found: uniquePlaces.slice(0, 6).map(([where, value]) => [capital(where), ': ', { q: clip(value, 110) }]),
        why: [['Wix templates come with sample text. This was never replaced, and it’s live: shoppers and search engines can read it.']],
        fix: (meta.pageType === 'product'
          ? [{ who: 'Product text', path: ['Products', '(this product)'], then: ['replace the sample description with your own.'] }]
          : meta.pageType === 'category'
            ? [{ who: 'Category text', path: ['Categories', '(this category)'], then: ['replace the description.'] }]
            : []).concat([
          { text: ['Text on the page itself: select it in the editor and type over it.'] },
          { text: ['Search and social text: the page’s SEO settings (', { b: 'Basics' }, ' and ', { b: 'Social share' }, ').'] }
        ]),
        sourceKeys: []
      }));
    }

    // Main headings.
    const h1s = facts.headings.filter(heading => heading.level === 1);
    const h1Line = (heading, index) => {
      const note = /product-title/i.test(heading.hook) ? ' (product name)' : /post-title/i.test(heading.hook) ? ' (post title)' : '';
      return [`${index + 1}. `, heading.text ? { q: clip(heading.text, 80) } : 'Empty', note];
    };
    if (!h1s.length) {
      add(finding('page', 'h1-none', 'check', 'This page has no main heading (H1).', {
        partial: 'a main heading (H1)',
        why: [['The H1 is the page’s main heading. Wix says it acts as the title of each page.']],
        fix: [{ text: ['Make the page’s main title an H1:'] }].concat(HEADING_STEPS(meta)),
        sourceKeys: ['wix-headings']
      }));
    } else if (h1s.length === 1) {
      add(finding('page', 'h1-one', 'fine', 'The page has one main heading (H1).', {
        found: [h1Line(h1s[0], 0).slice(1)],
        why: [['The H1 is the page’s main heading. Wix says it acts as the title of each page.']],
        sourceKeys: ['wix-headings']
      }));
    } else {
      const product = h1s.some(heading => /product-title/i.test(heading.hook));
      add(finding('page', 'h1-many', 'check', `This page has ${countWord(h1s.length).toLowerCase()} main headings (H1).`, {
        found: h1s.slice(0, 8).map(h1Line).concat(h1s.length > 8 ? [[`and ${h1s.length - 8} more`]] : []),
        why: [
          ['The H1 is the page’s main heading. Wix says: “H1s added to a site header will appear on all pages where the header is displayed. We therefore recommend adding headings to the page body instead.” Wix’s SEO Hub also advises one H1.'],
          product ? ['On store product pages, Wix usually makes the product name the H1, so the others often come from your header or footer.'] : null,
          h1s.length >= 6 ? ['With this many, headings are probably being used for styling.'] : null
        ].filter(Boolean),
        fix: [{ text: ['Change the extra ones to a smaller heading or plain text:'] }].concat(HEADING_STEPS(meta)),
        sourceKeys: ['wix-structure', 'wix-headings', 'wix-h1-hub']
      }));
    }
    const empty = facts.headings.filter(heading => !heading.text);
    if (empty.length) {
      add(finding('page', 'headings-empty', 'info', `${countWord(empty.length)} ${empty.length === 1 ? 'heading is' : 'headings are'} empty.`, {
        found: [[empty.map(heading => `H${heading.level}`).slice(0, 10).join(', ')]],
        why: [['Screen readers announce headings, so an empty one is a stop with nothing in it.']]
      }));
    }
    let skip = null;
    let previous = 0;
    facts.headings.forEach(heading => {
      if (!skip && previous && heading.level > previous + 1) skip = [previous, heading.level];
      previous = heading.level;
    });
    if (skip) {
      add(finding('page', 'headings-skip', 'info', `Heading levels skip: an H${skip[1]} comes right after an H${skip[0]}.`, {
        why: [['Screen readers use heading levels to move around a page. Skipped levels are minor.']]
      }));
    }

    // Pictures.
    const images = facts.images.filter(image => !image.noscript && !isPixel(image) && (image.src || image.srcset) && !(image.src.startsWith('data:') && !image.srcset));
    const noAlt = images.filter(image => image.alt == null);
    const emptyAlt = images.filter(image => image.alt != null && !tidy(image.alt));
    // Grids and sliders of other products: Wix doesn't say where their alt
    // text comes from, so these are a note, not a check.
    const inGrid = image => image.grid || Boolean(image.link && /\/product-page\//i.test(image.link));
    const gridEmpty = emptyAlt.filter(inGrid);
    const productEmpty = emptyAlt.filter(image => !inGrid(image) && image.productCard);
    const fileAlt = images.filter(image => image.alt && altIsFileName(image.alt));
    const fileAltSet = new Set(fileAlt);
    const productAltSteps = [{ who: 'Product pictures', path: ['Products', '(the product)', 'Images and videos', 'More Actions', 'Edit alt text'], then: ['write what the picture shows. ', { q: 'Copy to all images' }, ' uses the same text for every picture of that product.'] }];
    const pictureName = image => {
      if (image.link && /\/product-page\//i.test(image.link)) return ['Picture linking to ', { code: clip(pathOnly(image.link, true), 70) }];
      const name = humanFileName(image.src);
      return ['Picture ', { code: clip(name || pathOnly(image.src), 70) }];
    };
    if (noAlt.length) {
      add(finding('page', 'images-no-alt', 'check', `${countWord(noAlt.length)} ${noAlt.length === 1 ? 'picture has' : 'pictures have'} no alt text at all.`, {
        found: noAlt.slice(0, 5).map(pictureName),
        why: [['Alt text describes a picture in words, for search engines and screen readers. Without it, neither can tell what the picture shows.']],
        fix: noAlt.some(image => image.link && /\/product-page\//i.test(image.link)) ? productAltSteps : [],
        sourceKeys: []
      }));
    }
    if (productEmpty.length) {
      add(finding('page', 'images-product-empty-alt', 'check', `${countWord(productEmpty.length)} product ${productEmpty.length === 1 ? 'picture has' : 'pictures have'} empty alt text.`, {
        found: productEmpty.slice(0, 5).map(pictureName).concat(productEmpty.length > 5 ? [[`and ${productEmpty.length - 5} more`]] : []),
        why: [
          ['Alt text describes a picture in words, for search engines and screen readers. These pictures show products but have ', { code: 'alt=""' }, ', which tells both to skip them.'],
          ['Wix can’t write alt text from the file name or product name for you.']
        ],
        fix: productAltSteps,
        sourceKeys: ['wix-alt-auto']
      }));
    }
    if (gridEmpty.length) {
      add(finding('page', 'images-grid-empty-alt', 'info', `${countWord(gridEmpty.length)} product ${gridEmpty.length === 1 ? 'picture' : 'pictures'} in grids or sliders ${gridEmpty.length === 1 ? 'has' : 'have'} empty alt text.`, {
        found: gridEmpty.slice(0, 5).map(pictureName).concat(gridEmpty.length > 5 ? [[`and ${gridEmpty.length - 5} more`]] : []),
        why: [
          ['Alt text describes a picture in words, for search engines and screen readers. These pictures have ', { code: 'alt=""' }, ', which tells both to skip them.'],
          ['Wix can’t write alt text from the file name or product name for you.']
        ],
        fix: [{ text: ['Wix doesn’t say whether a product’s alt text reaches its grids. Add alt text to one product, publish, and check this page’s source again.'] }].concat(productAltSteps),
        sourceKeys: ['wix-alt-auto']
      }));
    }
    if (fileAlt.length) {
      add(finding('page', 'images-filename-alt', 'check', `${countWord(fileAlt.length)} ${fileAlt.length === 1 ? 'picture uses' : 'pictures use'} a file name as alt text.`, {
        found: fileAlt.slice(0, 5).map(image => [{ q: clip(image.alt, 80) }]).concat(fileAlt.length > 5 ? [[`and ${fileAlt.length - 5} more`]] : []),
        why: [['Alt text should say what the picture shows. A file name tells search engines and screen readers nothing about it.']],
        fix: fileAlt.some(image => (image.link && /\/product-page\//i.test(image.link)) || image.productCard) ? productAltSteps : [],
        sourceKeys: []
      }));
    }
    // Pictures whose alt text is already reported as a file name aren't repeated here.
    const camera = [];
    const cameraSeen = new Set();
    images.forEach(image => {
      if (fileAltSet.has(image)) return;
      const name = humanFileName(image.src);
      if (name && cameraFileName(name) && !cameraSeen.has(name)) { cameraSeen.add(name); camera.push(name); }
    });
    if (camera.length) {
      add(finding('page', 'images-file-names', 'info', `${countWord(camera.length)} picture ${camera.length === 1 ? 'file has' : 'files have'} a camera or screenshot name.`, {
        found: camera.slice(0, 5).map(name => [{ code: clip(name, 70) }]),
        why: [['A file name can say what a picture shows; these still have the name a camera or screenshot gave them. Name new uploads after what they show. Product photos are different: Wix says the file name doesn’t appear in their address.']],
        sourceKeys: ['wix-media-url']
      }));
    }
    if (!noAlt.length && !productEmpty.length && !fileAlt.length) {
      const described = images.filter(image => tidy(image.alt || '')).length;
      const decorative = images.length - described;
      add(finding('page', 'images-ok', 'fine', images.length ? 'Every picture has alt text or is marked as decoration.' : 'The source holds no pictures to check.', {
        found: images.length ? [[`${plural(images.length, 'picture')} in the source: ${described} with alt text`].concat(decorative ? [`, ${decorative} marked as decoration (`, { code: 'alt=""' }, ').'] : ['.'])] : [],
        why: meta.isWix ? [['Some Wix widgets, like product grids, can add their pictures with JavaScript after the page loads. So the live page can show more pictures than the source holds.'], ['Wix loads pictures lazily on purpose, and that isn’t a problem.']] : [['Alt text describes a picture in words, for search engines and screen readers. An empty one marks a picture as decoration.']],
        sourceKeys: meta.isWix ? ['wix-lazy'] : []
      }));
    }

    // Links.
    const pageUrl = meta.checkUrl || meta.canonicalUrl;
    const freeHosts = [];
    let internal = 0;
    let external = 0;
    let freeLinks = 0;
    let banner = 0;
    let unnamed = 0;
    const otherHosts = new Map();
    facts.anchors.forEach(anchor => {
      const href = anchor.href;
      if (!href || href.startsWith('#') || /^(javascript|mailto|tel|sms):/i.test(href)) return;
      const url = parseUrl(href, pageUrl ? pageUrl.href : 'https://page.invalid/');
      if (!url) return;
      if (!anchor.text && !anchor.label && !anchor.imageAlt) unnamed += 1;
      if (/(^|\.)wix\.com$/i.test(url.hostname) && /lpviral|enviral/i.test(url.pathname + url.search)) { banner += 1; return; }
      const same = url.hostname === 'page.invalid' || (pageUrl && bareHost(url.hostname) === bareHost(pageUrl.hostname));
      if (same) internal += 1;
      else {
        external += 1;
        otherHosts.set(bareHost(url.hostname), (otherHosts.get(bareHost(url.hostname)) || 0) + 1);
        if (isFreeHost(url.hostname) && pageUrl && !isFreeHost(pageUrl.hostname)) {
          freeLinks += 1;
          if (!freeHosts.includes(url.hostname)) freeHosts.push(url.hostname);
        }
      }
    });
    if (internal + external + banner) {
      const top = Array.from(otherHosts.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([host]) => host);
      const lines = [];
      if (top.length) lines.push(['Other sites: ', top.join(', ')]);
      if (freeLinks) {
        const where = `${freeHosts.slice(0, 2).join(', ')}${freeHosts.length > 2 ? ', …' : ''}`;
        lines.push([freeLinks === 1
          ? `1 link points to a free Wix address (${where}). If it’s your own, Wix redirects it to your domain, so link to your domain instead.`
          : `${plural(freeLinks, 'link')} point to free Wix addresses (${where}). If they’re your own, Wix redirects them to your domain, so link to your domain instead.`]);
      }
      if (unnamed) lines.push([`${plural(unnamed, 'link')} ${unnamed === 1 ? 'has' : 'have'} no text a screen reader can read.`]);
      if (banner) lines.push(['One link is Wix’s free-plan banner.']);
      const away = external + banner;
      const total = internal + away;
      const title = !away ? `${plural(total, 'link')}, ${total === 1 ? 'to this site' : 'all to this site'}.`
        : !internal ? `${plural(total, 'link')}, ${total === 1 ? 'to another site' : 'all to other sites'}.`
          : `${plural(total, 'link')}: ${internal} to this site, ${away} to ${away === 1 ? 'another site' : 'other sites'}.`;
      add(finding('page', 'links', 'info', title, {
        found: lines,
        why: [['Search engines find pages by following links. Google can follow a link when it’s a real ', { code: '<a href>' }, ' link.']],
        sourceKeys: ['google-links']
      }));
    }
  }

  // An address without its scheme; `bare` drops the host too (links within the store).
  function pathOnly(value, bare) {
    try {
      const url = new URL(value, 'https://page.invalid/');
      return url.hostname === 'page.invalid' || bare ? url.pathname : `${url.hostname}${url.pathname}`;
    } catch (_) { return String(value); }
  }

  function readBlocks(facts) {
    return facts.ldjson.map((entry, index) => {
      const raw = entry.text;
      let text = raw.trim();
      if (text.startsWith('<!--')) text = text.replace(/^<!--/, '').replace(/-->$/, '').trim();
      const data = text ? readJson(text) : null;
      const block = { index: index + 1, region: entry.region, empty: !text, error: null, nodes: [], text: '' };
      if (text && data === null && text !== 'null') {
        block.error = jsonProblem(text) || { message: 'The browser couldn’t read this JSON', line: 1, column: 1, before: '', at: '', after: '' };
        block.text = text.slice(0, 20000);
      } else if (data) block.nodes = nodesOf(data);
      return block;
    });
  }

  function checkData(ctx) {
    const { meta, add, blocks } = ctx;
    const where = block => (block.region ? `, in Custom Code (${POSITION_WORDS[block.region]})` : '');
    const blockName = block => `Block ${block.index} of ${blocks.length}${where(block)}`;
    const broken = blocks.filter(block => block.error);
    if (broken.length) {
      const policy = broken.some(block => /MerchantReturnPolicy|ShippingService|shippingDetails/i.test(block.text));
      const ordered = [];
      broken.forEach(block => {
        const err = block.error;
        ordered.push([`${blockName(block)}: line ${err.line}, column ${err.column}. ${err.message}.`]);
        if (err.before || err.at || err.after) ordered.push([{ mark: { before: err.before, at: err.at, after: err.after } }]);
      });
      add(finding('data', 'jsonld-error', 'fix', broken.length === 1 ? 'A structured data block has a JSON error.' : `${countWord(broken.length)} structured data blocks have JSON errors.`, {
        found: ordered,
        why: [['Structured data is code that describes the page to search engines, like a product’s price and stock. Search engines can’t read a block with a JSON error, so they skip it.']].concat(meta.isWix ? [['Wix’s own markup is usually valid JSON, so this most likely came from custom markup, Custom Code or an app.']] : []),
        // A block between the Custom Code markers is in Custom Code; any other
        // broken block is custom markup (or an app).
        fix: [
          broken.some(block => block.region) ? { text: ['It sits in your Custom Code: ', { path: CUSTOM_CODE }, ' (Development & integrations). Fix the JSON there.'] } : null,
          broken.some(block => !block.region) ? (meta.pageType === 'product' ? { who: 'Custom markup on this product', path: PRODUCT_SEO('Advanced').concat(['Structured data markup']) } : { who: 'Custom markup on this page', path: EDITOR_PATHS[editorOrder(meta)[0]].concat(['Advanced SEO', 'Structured Data Markup']) }) : null,
          { text: ['Then paste the code into Google’s ', { a: 'Rich Results Test', href: RICH_TEST }, ' to check it.', broken.some(block => !block.region) ? ' Wix takes up to 5 markups per page (counting its own), each under 7,000 characters and 8,000 in total.' : ''] }
        ].filter(Boolean),
        links: policy ? [GUIDES.policyTool, GUIDES.returns] : [],
        sourceKeys: ['wix-markup', 'wix-markup-trouble', 'google-sd-policies']
      }));
    }
    const nodes = [];
    blocks.forEach(block => block.nodes.forEach(node => nodes.push({ block, node, wix: meta.isWix && wixShaped(node) })));
    if (blocks.some(block => block.empty)) {
      add(finding('data', 'jsonld-empty', 'check', 'A structured data block is empty.', {
        why: [['An empty block does nothing. It may be left over from code someone removed.']],
        fix: [{ text: ['Look in ', { path: CUSTOM_CODE }, ' and in the page’s ', { b: 'Advanced SEO' }, ' tab.'] }],
        sourceKeys: ['wix-markup']
      }));
    }
    if (!nodes.length && !broken.length) {
      if (meta.pageType === 'product') {
        add(finding('data', 'jsonld-none', 'check', 'This product page has no Product markup.', {
          partial: 'structured data',
          why: [[meta.isWix ? 'Wix adds Product markup to store product pages by itself, so it was probably turned off. ' : '', 'Without it, Google can’t read the price and stock from structured data.']],
          fix: meta.isWix ? [{ who: 'This product', path: PRODUCT_SEO('Advanced').concat(['Structured data markup']), then: ['make sure the Product preset is included, not excluded.'] }] : [],
          sourceKeys: meta.isWix ? ['wix-presets', 'google-merchant'] : ['google-merchant']
        }));
      } else {
        add(finding('data', 'jsonld-none', 'info', 'This page has no structured data.', {
          partial: 'structured data',
          why: [['That’s common on plain pages. Structured data is code that describes the page to search engines, like a product’s price and stock.']],
          sourceKeys: meta.isWix ? ['wix-presets'] : []
        }));
      }
      return;
    }
    const wixNodes = nodes.filter(item => item.wix);
    const customNodes = nodes.filter(item => !item.wix);
    const typeList = list => Array.from(new Set(list.map(item => typesOf(item.node)[0] || 'images only'))).join(', ');
    if (wixNodes.length) {
      add(finding('data', 'jsonld-wix', 'info', `Wix wrote ${wixNodes.length === 1 ? 'this page’s structured data' : `${wixNodes.length} structured data blocks`}: ${typeList(wixNodes)}.`, {
        why: [['Wix writes this automatically. Its oddities, like a price written as text, can’t be fixed unless you convert it to custom markup. They aren’t errors.']],
        sourceKeys: ['wix-presets', 'wix-markup']
      }));
    }
    if (customNodes.length && meta.isWix) {
      add(finding('data', 'jsonld-custom', 'info', `Other structured data on this page: ${typeList(customNodes)}.`, {
        found: customNodes.slice(0, 6).map(item => [`${blockName(item.block)}: `, typesOf(item.node).join(', ') || 'no @type']),
        why: [
          ['Wix’s usual Product, ItemList, BlogPosting, WebSite and LocalBusiness blocks have a known shape; these don’t. They may be another Wix preset (events, bookings, blog article types, AI blog markup), custom markup, Custom Code or an app.'],
          ['Wix takes up to 5 markups per page (counting its own), each under 7,000 characters and 8,000 in total.']
        ],
        sourceKeys: ['wix-presets', 'wix-markup', 'wix-markup-trouble']
      }));
    } else if (customNodes.length) {
      add(finding('data', 'jsonld-found', 'info', `Structured data on this page: ${typeList(customNodes)}.`, {
        found: customNodes.slice(0, 6).map(item => [`${blockName(item.block)}: `, typesOf(item.node).join(', ') || 'no @type']),
        why: [['Structured data is code that describes the page to search engines, like a product’s price and stock.']],
        sourceKeys: ['google-sd-policies']
      }));
    }
    // A block missing @context or @type is reported once, however many
    // nodes it holds (a @graph can hold many).
    const reported = { context: new Set(), type: new Set(), images: new Set() };
    nodes.forEach(item => {
      const node = item.node;
      const index = item.block.index;
      if (!node['@context'] && !item.wix && !reported.context.has(index)) {
        reported.context.add(index);
        add(finding('data', `no-context-${index}`, 'check', `${blockName(item.block)} has no @context.`, {
          why: [['Without ', { code: '"@context": "https://schema.org"' }, ', search engines can’t tell which vocabulary the block uses.']],
          sourceKeys: ['google-sd-policies']
        }));
      }
      if (!node['@type'] && !item.wix && !reported.type.has(index)) {
        reported.type.add(index);
        add(finding('data', `no-type-${index}`, 'check', `${blockName(item.block)} has no @type.`, {
          why: [['Search engines need ', { code: '@type' }, ' to know what the block describes.']],
          sourceKeys: ['google-sd-policies']
        }));
      }
      if (!node['@type'] && item.wix && !reported.images.has(index)) {
        reported.images.add(index);
        add(finding('data', `images-block-${index}`, 'info', 'One block lists only images, with no @type.', {
          why: [['Wix writes blocks like this on some pages. It isn’t an error.']]
        }));
      }
    });

    // Products.
    const products = nodes.filter(item => hasType(item.node, 'Product'));
    const describeProduct = item => {
      const offer = asArray(item.node.offers)[0] || {};
      const bits = [item.node.name ? { q: clip(String(item.node.name), 60) } : 'no name'];
      const price = priceText(offer);
      if (price) bits.push(`, ${price}${currencyText(offer) ? ` ${currencyText(offer)}` : ''}`);
      const availability = availabilityName(offer.availability);
      if (availability) bits.push(`, ${availability}`);
      if (item.node.aggregateRating) bits.push(', with ratings');
      if (meta.isWix) bits.push(item.wix ? ' (Wix’s own block)' : ' (not in Wix’s shape)');
      return bits;
    };
    if (products.length > 1) {
      add(finding('data', 'product-duplicate', 'check', `This page has ${countWord(products.length).toLowerCase()} Product blocks.`, {
        found: products.map((item, index) => [`${index + 1}. `].concat(describeProduct(item))),
        why: [['Google’s guides don’t say how it treats two Product blocks on one page. Google’s Merchant Center asks for one offer per landing page, or each offer tagged with a SKU or GTIN. One block may come from an app, Custom Code or custom markup.']],
        fix: [
          { text: ['Keep one. Look for the other in:'] },
          { who: 'Custom markup', path: PRODUCT_SEO('Advanced').concat(['Structured data markup']) },
          { who: 'Custom Code', path: CUSTOM_CODE },
          { text: ['The settings of any reviews or product app.'] }
        ],
        sourceKeys: ['google-mc-markup', 'wix-markup']
      }));
    }
    products.forEach((item, index) => {
      const node = item.node;
      const label = products.length > 1 ? `Product block ${index + 1}` : 'The Product markup';
      const offers = asArray(node.offers).filter(offer => offer && typeof offer === 'object');
      const problems = [];
      if (!node.name) problems.push(['No ', { code: 'name' }, '.']);
      if (!node.image || (Array.isArray(node.image) && !node.image.length)) problems.push(['No ', { code: 'image' }, '.']);
      if (!offers.length) problems.push(['No ', { code: 'offers' }, ' (price and stock).']);
      offers.slice(0, 3).forEach(offer => {
        if (hasType(offer, 'AggregateOffer')) { problems.push(['The offer is an AggregateOffer. Merchant listings need a single ', { code: 'Offer' }, '.']); return; }
        const price = priceText(offer);
        if (!price) problems.push(['No ', { code: 'price' }, '.']);
        else if (!/^\d+(\.\d+)?$/.test(price)) problems.push(['Price ', { q: price }, ' isn’t a plain number. Use digits and a period, with no currency sign.']);
        else if (Number(price) <= 0) problems.push(['Price is ', { code: price }, '. Merchant listings need a price above 0.']);
        const currency = currencyText(offer);
        if (!currency) problems.push(['No ', { code: 'priceCurrency' }, '.']);
        else if (!/^[A-Z]{3}$/.test(currency)) problems.push(['Currency ', { q: currency }, ' isn’t a three-letter code like USD.']);
        if (offer.availability != null && !availabilityName(offer.availability)) problems.push(['Availability ', { q: String(offer.availability) }, ' isn’t one of the values Google lists, such as InStock or OutOfStock.']);
      });
      if (problems.length) {
        add(finding('data', `product-required-${index + 1}`, node.name ? 'check' : 'fix', `${label} is missing ${problems.length === 1 ? 'something' : `${countWord(problems.length).toLowerCase()} things`} Google needs for merchant listings.`, {
          found: [describeProduct(item)].concat(problems),
          why: [['For merchant listings, Google requires a name, an image, and an offer with a price above 0 and a currency. It also lists the availability values it accepts. Meeting them doesn’t guarantee a rich result.']],
          fix: item.wix ? [{ text: ['Wix fills these from the product itself: check the product’s price and pictures in ', { path: ['Products', '(this product)'] }, '.'] }] : [{ text: ['Fix it where the block was added: custom markup, Custom Code or the app that adds it.'] }],
          sourceKeys: ['google-merchant', 'schema-availability']
        }));
      } else {
        const offer = offers[0];
        add(finding('data', `product-ok-${index + 1}`, 'fine', `${label} has what Google requires for merchant listings.`, {
          found: [describeProduct(item)],
          why: [['Google decides whether to show rich results. Even complete markup doesn’t guarantee one.']],
          sourceKeys: ['google-merchant', 'google-sd-policies']
        }));
        const absent = [];
        if (!node.brand) absent.push('brand');
        if (!node.gtin && !node.gtin8 && !node.gtin12 && !node.gtin13 && !node.gtin14 && !node.isbn && !node.mpn) absent.push('gtin or mpn');
        if (!node.sku) absent.push('sku');
        if (!offer.itemCondition) absent.push('itemCondition');
        if (!offer.priceValidUntil) absent.push('priceValidUntil');
        const policyElsewhere = nodes.some(other => other.node !== node && (other.node.hasMerchantReturnPolicy || other.node.hasShippingService));
        if (!offer.shippingDetails && !policyElsewhere) absent.push('shippingDetails');
        if (!offer.hasMerchantReturnPolicy && !node.hasMerchantReturnPolicy && !policyElsewhere) absent.push('hasMerchantReturnPolicy');
        if (absent.length && index === products.findIndex(other => other.wix || products.length === 1)) {
          add(finding('data', 'product-recommended', 'info', 'The Product markup leaves out some fields Google recommends.', {
            found: [['Not there: ', joinWords(absent), '.']],
            why: [['These are recommended, not required.', meta.isWix ? ' Wix’s Product preset doesn’t write shipping or return details.' : '', ' You can add return and shipping policy markup yourself.']],
            links: [GUIDES.policyTool, GUIDES.returns],
            sourceKeys: meta.isWix ? ['google-merchant', 'wix-defaults'] : ['google-merchant']
          }));
        }
      }
    });

    nodes.forEach(item => {
      const node = item.node;
      if (hasType(node, 'LocalBusiness')) {
        const address = node.address && typeof node.address === 'object' ? node.address : null;
        const filled = address && ['streetAddress', 'addressLocality', 'addressRegion', 'postalCode', 'addressCountry'].some(key => address[key]);
        if (!filled && typeof node.address !== 'string') {
          add(finding('data', 'localbusiness-address', 'check', 'The LocalBusiness markup has no address.', {
            found: [['Name: ', node.name ? { q: clip(String(node.name), 60) } : 'none']],
            why: [['Google requires an address and a name for LocalBusiness markup. Wix builds this block from your business info.']],
            fix: [{ text: ['Add the address in your dashboard: ', { path: ['Settings', 'Business info'] }, ', under ', { q: 'Location and contact info' }, '.'] }],
            sourceKeys: ['google-local-business', 'wix-local-business']
          }));
        }
      }
      if (hasType(node, 'BreadcrumbList')) {
        const items = asArray(node.itemListElement);
        const bad = items.length < 2 || items.some((entry, i) => !entry || entry.position == null || !(entry.name || (entry.item && entry.item.name)) || (i < items.length - 1 && !entry.item));
        if (bad) {
          add(finding('data', 'breadcrumb-positions', 'check', 'The breadcrumb markup is missing positions, names or links.', {
            why: [['Google’s breadcrumb markup needs at least two items, each with a position and a name, and a link on every item but the last.']],
            sourceKeys: ['google-breadcrumb']
          }));
        }
      }
      if (hasType(node, 'FAQPage')) {
        add(finding('data', 'faq-retired', 'info', 'FAQ markup found. Google no longer shows FAQ rich results.', {
          why: [['Google stopped showing FAQ rich results in May 2026. The markup is harmless to keep.']],
          sourceKeys: ['google-updates']
        }));
      }
    });
  }

  // The catalog names real apps; the two made-up pop-up apps in the problem
  // sample are described here so the sample can show the overlap check.
  function classify(app) {
    const id = String(app.id || '').toLowerCase();
    if (id === SAMPLE_APP_IDS.popups || id === SAMPLE_APP_IDS.wheel) return { kind: 'listed', name: String(app.name || '').trim(), short: String(app.name || '').trim(), job: 'popups' };
    return catalog.classifyApp(app);
  }

  function checkApps(ctx) {
    const { facts, meta, add, model } = ctx;
    const apps = facts.modelText ? catalog.appsFromViewerModel(facts.modelText) : null;
    const snippets = facts.modelText ? catalog.customCodeFromViewerModel(facts.modelText) : null;
    const classified = (apps || []).map(app => Object.assign({ id: app.id }, classify(app)));
    // An app counts by its own name, or by the vendor's name when the catalog
    // knows the exact listing or id ("Contact Collection" is Mailchimp's).
    // Names are matched as whole words, so "Brand Clarity Toolkit" isn't
    // Microsoft Clarity.
    const listingNames = new Set(catalog.LISTED.map(entry => catalog.normalize(entry[0])));
    const vendorName = app => (app.short && (catalog.KNOWN_IDS[catalog.normalize(app.id)] || listingNames.has(catalog.normalize(app.name))) ? app.short : null);
    const installedNames = classified.flatMap(app => [app.name, vendorName(app)].filter(Boolean).map(catalog.normalize));
    const escapeRe = text => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const wordIn = (word, name) => new RegExp(`(^|[^a-z0-9])${escapeRe(word)}([^a-z0-9]|$)`).test(name);
    const installed = words => words.some(word => installedNames.some(name => wordIn(word, name)));
    if (!apps) {
      add(finding('apps', 'apps-missing', 'check', 'This source has no app list: it may be cut short, or not a Wix page.', {
        why: [['Wix pages carry a list of the site’s apps near the end of the source. If you copied only part of the page, open View page source again, select all and copy.']],
        sourceKeys: ['wix-source']
      }));
    } else {
      const order = { listed: 0, other: 1, wix: 2, 'wix-standard': 3 };
      const sorted = classified.slice().sort((a, b) => order[a.kind] - order[b.kind] || a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      const label = app => {
        if (app.kind === 'wix-standard') return 'Wix’s own, comes with a Wix store';
        if (app.kind === 'wix') return app.job ? `Wix’s own: ${catalog.JOBS[app.job]}` : 'Wix’s own';
        if (app.kind === 'listed') return app.job ? `${catalog.JOBS[app.job]} (third-party)` : 'Third-party app';
        return 'Other app';
      };
      // Apps from other companies first: they're the ones a store owner adds
      // and forgets. A long list shows ten and folds the rest.
      const outside = sorted.filter(app => app.kind === 'listed' || app.kind === 'other');
      const own = sorted.filter(app => app.kind === 'wix' || app.kind === 'wix-standard');
      const row = app => [[{ b: app.name }], [label(app)]];
      const more = [];
      if (outside.length > 10) more.push({ label: `${outside.length - 10} more ${outside.length - 10 === 1 ? 'app' : 'apps'} not marked as Wix’s own`, rows: outside.slice(10).map(row) });
      if (own.length) more.push({ label: `${own.length} of Wix’s own ${own.length === 1 ? 'app' : 'apps'}`, rows: own.map(row) });
      add(finding('apps', 'apps-list', 'info', apps.length ? `${plural(apps.length, 'app is', 'apps are')} installed on this site.` : 'No apps are listed for this site.', {
        pin: true,
        found: apps.length ? [[`Wix marks ${own.length} of them as its own.`]] : [],
        table: apps.length ? { caption: 'Apps installed on this site', cols: ['App', 'What it is'], rows: outside.slice(0, 10).map(row), more } : null,
        why: [['This list is for the whole site: Wix puts the site’s app list in each page’s source. Wix says it can’t optimize third-party apps, and that each app you add brings its own JavaScript.']],
        fix: apps.length ? [
          { who: 'Remove an app you don’t use', path: ['Manage Apps', '(the app)', 'More Actions', 'Delete'] },
          { who: 'A full-page app in the Wix Editor', path: ['My Business', '(the app)', 'More Actions', 'Delete App'] },
          { text: ['Deleting an app doesn’t cancel its subscription, and can delete its data. Export what you need first.'] }
        ] : [],
        sourceKeys: ['wix-speed', 'wix-unused-js', 'wix-delete-apps']
      }));
      // The list says what is installed, not what is switched on, so the
      // risk is said with an "if".
      const risks = {
        popups: 'If both are switched on, shoppers can see both.',
        email: 'If both are switched on, shoppers can get email from both.',
        sms: 'If both are switched on, shoppers can get texts from both.',
        tracking: 'Both may add code to your pages for the same job.'
      };
      const verbs = { popups: 'show pop-ups and sign-up forms', email: 'do email marketing', sms: 'send texts or WhatsApp messages', tracking: 'record or measure visitors' };
      // Two listings from one company aren't two companies doing one job.
      catalog.overlaps(classified).filter(group => new Set(group.apps.map(app => catalog.normalize(app.short || app.name))).size > 1).forEach(group => {
        add(finding('apps', `app-overlap-${group.job}`, 'check', `${countWord(group.apps.length)} apps ${verbs[group.job]}.`, {
          found: group.apps.map(app => [{ b: app.name }, app.kind === 'wix' ? ' (Wix’s own)' : ' (third-party)']),
          why: [[risks[group.job], ' Wix says each app you add brings its own JavaScript, and it can’t optimize third-party apps.']],
          fix: [
            { text: ['If they do the same job, keep the one you use and delete the other in ', { b: 'Manage Apps' }, ' in your dashboard.'] },
            { text: ['Deleting an app doesn’t cancel its subscription.'] }
          ],
          links: group.job === 'popups' ? [GUIDES.popups, GUIDES.speed] : [GUIDES.speed],
          sourceKeys: ['wix-unused-js', 'wix-speed', 'wix-delete-apps']
        }));
      });
    }

    // Custom Code: the site's snippets from the page data (name, where, which
    // pages). If the page data lists none, the code written between this
    // page's Custom Code markers stands in, one row per area.
    const regionScripts = facts.scripts.filter(script => script.region);
    let rows = [];
    const fromModel = Boolean(snippets && snippets.length);
    if (fromModel) {
      rows = snippets.map(snippet => ({ name: snippet.name, position: snippet.position, pages: snippet.pages, vendors: vendorsIn(snippet.code), code: snippet.code }));
    } else if (regionScripts.length) {
      ['head', 'bodyStart', 'bodyEnd'].forEach(position => {
        const scripts = regionScripts.filter(script => script.region === position);
        if (!scripts.length) return;
        const code = scripts.map(script => `${script.src} ${script.code || ''}`).join('\n');
        rows.push({ name: '', count: scripts.length, position, pages: -1, vendors: vendorsIn(code), code });
      });
    }
    const pagesWord = row => (row.pages === -1 ? 'this page' : row.pages ? plural(row.pages, 'page') : 'all pages');
    const placeLine = row => (row.name
      ? ['Custom Code snippet ', { q: row.name }, ` (${POSITION_WORDS[row.position] || row.position}, ${pagesWord(row)})`]
      : [`Custom Code on this page (${POSITION_WORDS[row.position] || row.position})`]);
    if (rows.length) {
      const what = row => (row.vendors.length ? row.vendors.map(vendor => vendor.name).join(', ') : 'Not one we know: read it before deleting');
      add(finding('apps', 'custom-code', 'info', fromModel ? `Custom Code on this site: ${plural(rows.length, 'snippet')}.` : `Custom Code on this page: ${plural(regionScripts.length, 'script')}.`, {
        pin: true,
        found: fromModel ? [] : [['This source doesn’t list your snippets by name, so these are the scripts in the page’s Custom Code areas.']],
        table: {
          caption: 'Custom Code',
          cols: ['Snippet', 'What it is', 'Where it loads'],
          rows: rows.map(row => [[row.name ? { b: row.name } : { b: row.count ? plural(row.count, 'script') : '(no name)' }], [what(row)], [`${POSITION_WORDS[row.position] || row.position || '—'}, ${pagesWord(row)}`]])
        },
        why: [['Custom Code is code added in your site’s settings. Wix tools can add entries here too, such as a Google verification tag. Wix says snippets don’t appear as elements in the editor, so they’re easy to forget.']],
        fix: [
          { text: ['See and edit it in ', { path: CUSTOM_CODE }, ', in the Development & integrations section.'] },
          { text: ['To find what a snippet does, Wix suggests turning snippets off one at a time and testing your site after each change.'] }
        ],
        links: [GUIDES.leftover],
        sourceKeys: ['wix-custom-code', 'wix-custom-code-dev']
      }));
    } else if (apps) {
      add(finding('apps', 'custom-code', 'info', 'No Custom Code on this site.', {
        why: [['Custom Code is code added in your site’s settings, in ', { path: CUSTOM_CODE }, '.']],
        sourceKeys: ['wix-custom-code']
      }));
    }

    const tracking = rows.filter(row => row.vendors.some(vendor => vendor.tracking));
    if (tracking.length) {
      add(finding('apps', 'custom-code-tracking', 'check', 'Tracking code is pasted into Custom Code.', {
        found: tracking.slice(0, 8).map(row => placeLine(row).concat([`: ${row.vendors.filter(vendor => vendor.tracking).map(vendor => vendor.name).join(', ')}`])),
        why: [['Wix says: “Tracking scripts such as Google Analytics, Google Tag Manager, and Facebook & TikTok pixels do not work through custom code.” Wix’s place for them is Marketing Integrations.']],
        fix: [
          { text: ['Connect the tool in ', { b: 'Marketing Integrations' }, ' in your dashboard. It needs an upgraded site and a connected domain.'] },
          { text: ['Then turn this snippet off in ', { path: CUSTOM_CODE }, '.'] }
        ],
        sourceKeys: ['wix-custom-code', 'wix-integrations']
      }));
    }

    // Scripts that load from an address.
    const srcs = facts.scripts.filter(script => script.src).map(script => ({ src: script.src, region: script.region }))
      .concat(facts.links.filter(link => link.rel.includes('preload') && link.as === 'script' && link.href).map(link => ({ src: link.href, region: null })));
    // Hosts, counted; `loose` counts the scripts outside the Custom Code areas
    // (the ones inside are already listed as snippets above).
    const wixHosts = new Map();
    const outside = new Map();
    srcs.forEach(entry => {
      const host = hostOf(entry.src, meta.checkUrl ? meta.checkUrl.href : undefined);
      if (!host || host === 'page.invalid' || (meta.checkUrl && bareHost(host) === bareHost(meta.checkUrl.hostname))) return;
      const bucket = isWixHost(host) ? wixHosts : outside;
      const record = bucket.get(host) || { count: 0, loose: 0 };
      record.count += 1;
      if (!entry.region) record.loose += 1;
      bucket.set(host, record);
    });

    // Code from an app that isn't installed may be left over from one that was removed.
    if (apps) {
      const leftovers = new Map();
      rows.forEach(row => row.vendors.forEach(vendor => {
        if (vendor.app && !installed(vendor.app)) leftovers.set(vendor.key, (leftovers.get(vendor.key) || []).concat([placeLine(row)]));
      }));
      const looseHosts = new Map();
      outside.forEach((record, host) => {
        const vendor = VENDORS.find(item => item.app && item.test.test(host));
        if (vendor && record.loose && !installed(vendor.app)) {
          leftovers.set(vendor.key, (leftovers.get(vendor.key) || []).concat([['A script from ', { code: host }]]));
          if (!looseHosts.has(vendor.key)) looseHosts.set(vendor.key, host);
        }
      });
      leftovers.forEach((lines, key) => {
        const vendor = VENDOR_BY_KEY[key];
        const inCode = lines.some(line => String(line[0]).startsWith('Custom Code'));
        const title = inCode
          ? `Custom Code loads ${vendor.name}, and no ${vendor.name} app is installed.`
          : `A script from ${looseHosts.get(key)} (${vendor.name}) is in this page, and no ${vendor.name} app is installed.`;
        add(finding('apps', `leftover-${key}`, 'check', title, {
          found: lines,
          why: [[`If you use ${vendor.name} through this code, that’s fine. If not, it may be left over from an app you removed: Wix doesn’t state that removing an app removes every script it added.`]],
          fix: [
            inCode ? { who: 'If you don’t use it', path: CUSTOM_CODE, then: ['turn the snippet off and test your site. Wix suggests one snippet at a time.'] } : { who: 'If you don’t use it', path: CUSTOM_CODE, then: ['look for it there first.'] },
            { text: ['Check your pages for a widget the app left behind.'] },
            { text: ['Also look in ', { b: 'Marketing Integrations' }, ' in your dashboard, where Wix connects tracking tools.'] }
          ],
          links: [GUIDES.leftover],
          sourceKeys: ['wix-custom-code', 'wix-integrations', 'wix-delete-apps']
        }));
      });
    }

    const hostRows = Array.from(outside.entries()).sort((a, b) => b[1].count - a[1].count || a[0].localeCompare(b[0]))
      .map(([host, record]) => [[{ code: host }], [hostName(host) || 'Not one we know: search the address to see what it is'], [String(record.count)]]);
    const wixLine = Array.from(wixHosts.entries()).sort((a, b) => b[1].count - a[1].count || a[0].localeCompare(b[0])).map(([host, record]) => `${host} (${record.count})`).join(', ');
    add(finding('apps', 'scripts-hosts', 'info', outside.size ? `${countWord(outside.size)} outside ${outside.size === 1 ? 'address loads' : 'addresses load'} scripts on this page.` : 'No scripts load from outside Wix.', {
      pin: Boolean(outside.size),
      table: outside.size ? { caption: 'Scripts from outside Wix', cols: ['Address', 'What it is', 'Scripts'], rows: hostRows } : null,
      found: wixLine ? [['Loaded by Wix: ', wixLine, '.']] : [],
      why: [['Apps from the Wix App Market usually don’t show up as scripts: their code isn’t in the source. What shows here was written into the page, usually by Custom Code.']],
      links: [GUIDES.leftover],
      sourceKeys: ['wix-custom-code']
    }));

    const inline = facts.scripts.filter(script => !script.src && !script.marker).length;
    const loaded = facts.scripts.filter(script => script.src).length;
    add(finding('apps', 'scripts-size', 'info', `The source is ${sizeLabel(meta.bytes)}, with ${plural(facts.scripts.filter(script => !script.marker).length, 'script')}.`, {
      found: [[`${loaded} load from an address, ${inline} are written into the page.`]],
      why: [['Size and script counts don’t tell you how fast the page is. To see what an app costs, test the page with and without it. Wix’s own advice: “Limit the number of apps and widgets on each page.”']],
      fix: [{ text: ['Measure the page in ', { a: 'PageSpeed Insights', href: PSI }, ', then turn an app or snippet off and measure again.'] }],
      links: [GUIDES.speed],
      sourceKeys: ['wix-cwv', 'wix-speed-blog']
    }));
    return { apps, snippets: rows, model };
  }

  // ---- The whole check ----------------------------------------------------------------

  const ERROR_TITLE = /^(404 Error: Page Not Found|.+ Error \| Wix\.com|Checking Your Request|Just a moment\.*|Attention Required! \| Cloudflare|Access denied)$/i;

  function emptyResult(meta, input) {
    return { meta: Object.assign(meta, { input }), findings: [], counts: { fix: 0, check: 0, fine: 0, info: 0 }, notices: [] };
  }

  /* analyzePage(html, { url, sample, file, read }) → { meta, findings, counts, notices }
     url: what the visitor typed in "Page address" (optional).
     read: a reader (html → view); the default uses DOMParser in a browser. */
  function analyzePage(source, options = {}) {
    let html = String(source == null ? '' : source);
    if (html.charCodeAt(0) === 0xfeff) html = html.slice(1);
    const bytes = utf8Bytes(html);
    const meta = { isWix: false, wixSignals: [], pageType: 'unknown', special: null, editor: 'unknown', bytes, truncated: false, sample: options.sample || null, file: options.file || null, pageUrl: null, canonicalUrl: null, notAPage: null };
    if (!html.trim()) return emptyResult(meta, 'empty');
    if (bytes > MAX_BYTES) return emptyResult(meta, 'too-big');
    const head = html.slice(0, 4096);
    if (html.indexOf('<') === -1 || !/<(!doctype|html|head|body|meta|title|link|script|div|p|h1|img|a)\b/i.test(html.slice(0, 200000))) {
      return emptyResult(meta, /^\s*(view-source:)?https?:\/\/\S+\s*$/i.test(head) ? 'url' : 'not-html');
    }
    const read = options.read || defaultReader();
    const facts = collect(read(html));
    // A whole page ends with </html> (perhaps followed by a comment).
    meta.truncated = !/<\/html\s*>/i.test(html.slice(-4096));

    const model = readModel(facts.modelText);
    const essential = readModel(facts.essentialText);
    const generator = facts.metas.some(item => item.name === 'generator' && /wix\.com/i.test(item.content || ''));
    if (generator) meta.wixSignals.push('generator');
    if (facts.modelText) meta.wixSignals.push('viewer-model');
    if (facts.scripts.some(script => script.marker)) meta.wixSignals.push('custom-code-markers');
    if (facts.scripts.some(script => /(^|\.)parastorage\.com$/i.test(hostOf(script.src)))) meta.wixSignals.push('parastorage');
    if (facts.metas.some(item => /^x-wix-/.test(item.httpEquiv))) meta.wixSignals.push('wix-headers');
    meta.isWix = meta.wixSignals.length > 0;
    meta.editor = !meta.isWix ? 'unknown' : (model && model.studio) || /(^|\s)responsive(\s|$)/.test(facts.bodyClass) ? 'studio' : 'classic';

    // Two addresses. The page type reads the typed address first (pageUrl).
    // The tag checks compare with the address the source itself came from
    // (checkUrl), so a mistyped address can't invent a canonical problem.
    const typed = readAddress(options.url);
    const canonicalHref = (facts.links.find(link => link.rel.includes('canonical')) || {}).href;
    const requested = (model && model.requestUrl) || (essential && essential.requestUrl) || '';
    meta.canonicalUrl = parseUrl(canonicalHref) || null;
    meta.sourceUrl = parseUrl(requested) || null;
    meta.pageUrl = (typed.ok && typed.url) || meta.sourceUrl || null;
    meta.checkUrl = meta.sourceUrl || meta.pageUrl;
    meta.addressFrom = typed.ok && typed.url ? 'typed' : meta.sourceUrl ? 'source' : meta.canonicalUrl ? 'canonical' : 'none';
    const kindFrom = meta.pageUrl || meta.canonicalUrl || parseUrl(metaContent(facts, item => item.property === 'og:url')[0]);
    const kind = pageKind(kindFrom);
    meta.pageType = kind.type;
    meta.special = kind.special;
    const ogType = lower(metaContent(facts, item => item.property === 'og:type')[0]);
    if (meta.pageType === 'page' || meta.pageType === 'unknown') {
      if (ogType === 'product') meta.pageType = 'product';
      else if (ogType === 'article') meta.pageType = 'blog-post';
    }

    const notices = [];
    if (!model && !canonicalHref && (ERROR_TITLE.test(facts.titles[0] || '') || facts.errorMarker)) {
      meta.notAPage = facts.titles[0] || 'error page';
      const result = emptyResult(meta, 'ok');
      result.findings.push(finding('index', 'not-a-page', 'check', 'This looks like an error page, not one of your pages.', {
        found: [['Title: ', { q: facts.titles[0] || 'none' }]],
        why: [['Wix’s “page not found” and domain error pages, and the “Checking your request” screen some sites show, look like pages but aren’t yours. They carry no app list and no canonical tag.']],
        fix: [{ text: ['Open the page in a normal browser window and make sure it loads. Then open View page source again and copy all of it.'] }],
        sourceKeys: ['wix-source']
      }));
      result.counts = countFindings(result.findings);
      return result;
    }
    if (!meta.isWix) notices.push({ kind: 'not-wix', text: 'It has none of the signs Wix pages carry, like Wix’s generator tag and its page data. The search checks still ran; the app checks didn’t.' });
    if (meta.truncated) notices.push({ kind: 'truncated', text: 'This source may be cut short: it doesn’t end with </html>. Copy the whole page: select all, then copy.' });
    if (typed.ok && typed.url && meta.sourceUrl && (bareHost(typed.url.hostname) !== bareHost(meta.sourceUrl.hostname) || !samePath(typed.url, meta.sourceUrl))) {
      notices.push({ kind: 'address', text: `The address you typed isn’t the one this source came from (${shortUrl(meta.sourceUrl)}). The checks compare the tags with ${shortUrl(meta.sourceUrl)}.` });
    }
    if (options.file) notices.push({ kind: 'file', text: 'Checked a saved file. Pages saved with “Save page as” can differ from what Google gets; View page source is the safer copy.' });

    const findings = [];
    const add = item => findings.push(item);
    const blocks = readBlocks(facts);
    const ctx = { facts, meta, add, blocks, model };
    checkIndexing(ctx);
    checkSearch(ctx);
    checkPage(ctx);
    checkData(ctx);
    if (meta.isWix) checkApps(ctx);
    // A source cut short can't show that something is missing, only that it
    // isn't in what was pasted.
    const partial = meta.truncated && !facts.modelText;
    findings.forEach(item => {
      if (partial && item.partial) {
        item.status = 'info';
        item.title = `Not in what you pasted: ${item.partial}.`;
        item.why = [['What you pasted seems cut short, so this may be further down the page. Paste the whole source to check it.']].concat(item.why);
        item.fix = [];
      }
      delete item.partial;
    });
    findings.forEach((item, index) => { item.order = index; });
    findings.sort((a, b) => GROUPS.findIndex(group => group.id === a.group) - GROUPS.findIndex(group => group.id === b.group) || STATUS_ORDER[a.status] - STATUS_ORDER[b.status] || a.order - b.order);
    findings.forEach(item => { delete item.order; });
    return { meta: Object.assign(meta, { input: 'ok' }), findings, counts: countFindings(findings), notices };
  }

  function countFindings(findings) {
    const counts = { fix: 0, check: 0, fine: 0, info: 0 };
    findings.forEach(item => { counts[item.status] += 1; });
    return counts;
  }

  // ---- Made-up sample pages ---------------------------------------------------------
  // Two made-up stores, written in the structure real Wix pages have (the
  // head's SEO block, Custom Code markers, <wow-image>, the viewer model with
  // its app list near the end), shortened to the parts the checks read.

  const SAMPLES = Object.freeze({
    problems: Object.freeze({
      id: 'problems',
      button: 'Product page with problems',
      store: 'Juniper & Oak Candle Co.',
      url: 'https://www.juniperandoak.example/product-page/cedar-smoke-soy-candle'
    }),
    fine: Object.freeze({
      id: 'fine',
      button: 'Product page, mostly fine',
      store: 'Northfield Tea Supply',
      url: 'https://www.northfieldtea.example/product-page/lapsang-souchong-loose-leaf-tea'
    })
  });

  const scriptJson = value => JSON.stringify(value).replace(/\//g, '\\/').replace(/</g, '\\u003c');
  const media = (prefix, hash, ext, name, transform) => `https://static.wixstatic.com/media/${prefix}_${hash}~mv2.${ext}/v1/${transform}/${name || `${prefix}_${hash}~mv2.${ext}`}`;

  function sampleSource(id) {
    return id === 'fine' ? fineSample() : problemSample();
  }

  function pageHead({ title, description, canonical, siteName, og, ld, embedsHead, essential, modelSite }) {
    return `<!DOCTYPE html>
<!-- Made-up sample for the myuser.ai Wix page checker. ${esc(siteName)} is not a real store.
     Written in the structure of a real Wix page, shortened to the parts the checker reads. -->
<html lang="en">
<head>
  <meta charset='utf-8'>
  <meta name="viewport" content="width=device-width, initial-scale=1" id="wixDesktopViewport" />
  <meta http-equiv="X-UA-Compatible" content="IE=edge">
  <meta name="generator" content="Wix.com Website Builder"/>
  <link rel="icon" sizes="192x192" href="${media(modelSite.prefix, modelSite.icon, 'png', null, 'fill/w_192%2Ch_192%2Clg_1%2Cusm_0.66_1.00_0.01')}" type="image/png"/>
  <!-- Safari Pinned Tab Icon -->
  <!-- <link rel="mask-icon" href="${media(modelSite.prefix, modelSite.icon, 'png', null, 'fill/w_32%2Ch_32')}"> -->
  <script nomodule="" src="https://static.parastorage.com/unpkg/core-js-bundle@3.2.1/minified.js"></script>
  <script nomodule="" src="https://static.parastorage.com/unpkg/focus-within-polyfill@5.0.9/dist/focus-within-polyfill.js"></script>
  <!-- Essential Viewer Model -->
  <script type="application/json" id="wix-essential-viewer-model">${scriptJson(essential)}</script>
  <script>
    window.thunderboltVersion = "1.18015.0"
  </script>
  <!-- Initial CSS -->
  <style data-url="https://static.parastorage.com/services/wix-thunderbolt/dist/main.744ea815.min.css">/* Wix’s page styles: left out of this made-up sample */</style>
  <meta name="format-detection" content="telephone=no">
  <meta name="skype_toolbar" content="skype_toolbar_parser_compatible">
      <!--pageHtmlEmbeds.head start-->
      <script type="wix/htmlEmbeds" id="pageHtmlEmbeds.head start"></script>
${embedsHead}
      <script type="wix/htmlEmbeds" id="pageHtmlEmbeds.head end"></script>
      <!--pageHtmlEmbeds.head end-->
<meta http-equiv="X-Wix-Meta-Site-Id" content="00000000-0000-4000-8000-0000000000aa">
<meta http-equiv="X-Wix-Application-Instance-Id" content="00000000-0000-4000-8000-0000000000ab">
<meta http-equiv="X-Wix-Published-Version" content="418"/>
<!-- render-head end -->
<style data-href="https://static.parastorage.com/services/editor-elements-library/dist/thunderbolt/rb_wixui.thunderbolt_bootstrap.674e7dbc.min.css">/* left out */</style>
<title>${esc(title)}</title>
${description ? `  <meta name="description" content="${esc(description)}"/>\n` : ''}  <link rel="canonical" href="${esc(canonical)}"/>
  <meta name="robots" content="index"/>
  <meta property="og:title" content="${esc(title)}"/>
  <meta property="og:description" content="${esc(og.description)}"/>
  <meta property="og:image" content="${esc(og.image)}"/>
  <meta property="og:image:width" content="1200"/>
  <meta property="og:image:height" content="1200"/>
  <meta property="og:url" content="${esc(canonical)}"/>
  <meta property="og:site_name" content="${esc(siteName)}"/>
  <meta property="og:type" content="product"/>
  <meta property="og:availability" content="InStock"/>
${ld.map(block => `  <script type="application/ld+json">${block}</script>`).join('\n')}
  <meta property="product:price:amount" content="${esc(og.price)}"/>
  <meta property="product:price:currency" content="${esc(og.currency)}"/>
  <meta name="twitter:card" content="summary_large_image"/>
  <meta name="twitter:title" content="${esc(title)}"/>
  <meta name="twitter:description" content="${esc(og.description)}"/>
  <meta name="twitter:image" content="${esc(og.image)}"/>
<script type="speculationrules">{"prefetch":[{"tag":"mpa-prefetch-eager","where":{"href_matches":"\\/*"},"eagerness":"eager"}]}</script>
<style rel="stylesheet" data-href="https://static.parastorage.com/services/wixstores-client-product-page/1.4416.0/ProductPageViewerWidget.min.css">/* left out */</style>
</head>`;
  }

  function viewerModel({ apps, embeds, studio, requestUrl, baseUrl }) {
    const appsClientSpecMapData = {};
    apps.forEach(([id, name, wixOwn], index) => { appsClientSpecMapData[id] = { applicationId: index + 3, appDefinitionName: name, isWixTPA: wixOwn }; });
    return scriptJson({
      siteFeaturesConfigs: {
        accessibilityBrowserZoom: { isBuilder: false, isStudio: studio },
        tpaCommons: { appsClientSpecMapData, requestUrl, externalBaseUrl: baseUrl, isPremiumDomain: true, viewMode: 'site' },
        platform: { bootstrapData: { appsSpecData: { dataBinding: { appDefinitionId: 'dataBinding', type: 'application', appDefinitionName: 'Data Binding', isWixTPA: true } } } },
        codeEmbed: { htmlEmbeds: embeds.map((embed, index) => ({ id: `00000000-0000-4000-9000-00000000000${index}`, embedType: 'custom', pages: embed.pages, position: embed.position, loadOnce: false, content: { type: 'custom', html: embed.html, category: { value: 'ESSENTIAL' } }, name: embed.name })) }
      },
      requestUrl,
      language: { userLanguage: 'en', siteLanguage: 'en', isMultilingualEnabled: false },
      site: { externalBaseUrl: baseUrl, siteRevision: 418, isResponsive: studio, editorName: studio ? 'Studio' : 'Unknown', isSEO: false, appNameForBiEvents: studio ? 'wix-studio' : 'thunderbolt' },
      commonConfig: { brand: studio ? 'studio' : 'wix', host: 'VIEWER', locale: 'en-us' }
    });
  }

  const STORE_APPS = [
    ['215238eb-22a5-4c36-9e7b-e7c08025e04e', 'Wix Stores', true],
    ['1380b703-ce81-ff05-f115-39571d94dfcd', 'Checkout & Orders', true],
    ['135c3d92-0fea-1f9d-2ba5-2a1dfb04297e', 'ShoutOut (Legacy)', true],
    ['14ce1214-b278-a7e4-1373-00cebd1bef7c', 'Old Wix Forms and Payments', true],
    ['14cc59bc-f0b7-15b8-e1c7-89ce41d0e0c9', 'Wix Members Area', true],
    ['14cffd81-5215-0a7f-22f8-074b0e2401fb', 'Member Account Info', true],
    ['1505b775-e885-eb1b-b665-1e485d9bf90e', 'My Addresses', true],
    ['4aebd0cb-fbdb-4da7-b5d1-d05660a30172', 'My Wallet', true]
  ];

  // Made-up apps for the made-up samples: their ids and .example hosts can't
  // appear on a real Wix page. They let the samples show the app checks
  // without naming a real company.
  const SAMPLE_APP_IDS = {
    popups: '0e5a3c10-7b1d-4c2e-9f00-5a3c0000a001',
    wheel: '0e5a3c10-7b1d-4c2e-9f00-5a3c0000a002',
    bundles: '0e5a3c10-7b1d-4c2e-9f00-5a3c0000a003',
    reviews: '0e5a3c10-7b1d-4c2e-9f00-5a3c0000a004'
  };

  function problemSample() {
    const store = 'Juniper & Oak Candle Co.';
    const base = 'https://www.juniperandoak.example';
    const url = SAMPLES.problems.url;
    const name = 'Cedar & Smoke Soy Candle';
    const template = 'I’m a product description. I’m a great place to add more details about your product such as sizing, material, care and cleaning instructions.';
    const hero = ['a1b2c3', '5d0c2f7e9a8b4c1d9e6f3a2b1c0d9e8f', 'jpg'];
    const heroUrl = media(hero[0], hero[1], hero[2], 'file.jpg', 'fit/w_500,h_500,q_90');
    const wixProduct = JSON.stringify({
      '@context': 'https://schema.org/', '@type': 'Product', name, description: template, sku: 'JO-CS-08',
      image: [{ '@type': 'ImageObject', height: '1200', width: '1200', contentUrl: heroUrl, representativeOfPage: true, thumbnail: { '@type': 'ImageObject', height: '50', width: '50', contentUrl: media(hero[0], hero[1], hero[2], 'file.jpg', 'fit/w_50,h_50,q_90') } }],
      offers: { '@type': 'Offer', url, priceCurrency: 'USD', price: '28', availability: 'https://schema.org/InStock', seller: { '@type': 'Organization', name: store, sameAs: base } }
    });
    const appProduct = JSON.stringify({
      '@context': 'https://schema.org', '@type': 'Product', name, image: heroUrl,
      aggregateRating: { '@type': 'AggregateRating', ratingValue: 4.8, reviewCount: 37 },
      offers: { '@type': 'Offer', price: 28, priceCurrency: 'USD', availability: 'https://schema.org/InStock' }
    });
    const policy = `<script type="application/ld+json">
{
  "@context": "https://schema.org",
  "@type": "OnlineStore",
  "name": "${store}",
  "hasMerchantReturnPolicy": {
    "@type": "MerchantReturnPolicy",
    "applicableCountry": "US",
    "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
    "merchantReturnDays": 30,
    "returnFees": "https://schema.org/FreeReturn",
  }
}
</script>`;
    const forms = '<script async type="text/javascript" src="https://cdn.fernwhistle-forms.example/embed.js?shop=juniper-oak"></script>';
    const embeds = [
      { name: 'Return policy markup', position: 'head', html: policy },
      { name: 'Signup forms', position: 'bodyEnd', html: forms }
    ];
    const head = pageHead({
      title: `${name} | ${store}`,
      description: '',
      canonical: url,
      siteName: store,
      og: { description: template, image: heroUrl, price: '28', currency: 'USD' },
      ld: [wixProduct, appProduct],
      embedsHead: policy,
      essential: { site: { externalBaseUrl: base, isSEO: false }, requestUrl: url, language: { userLanguage: 'en' } },
      modelSite: { prefix: 'a1b2c3', icon: '0f1e2d3c4b5a69788796a5b4c3d2e1f0' }
    });
    const related = [['amber-glass-votive', 'Amber Glass Votive', '7c1d2e3f4a5b6c7d8e9f0a1b2c3d4e5f'], ['wood-wick-trio', 'Wood Wick Trio', '1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b'], ['linen-room-spray', 'Linen Room Spray', '9a8b7c6d5e4f3a2b1c0d9e8f7a6b5c4d']];
    const model = viewerModel({
      apps: STORE_APPS.concat([
        ['14517e1a-3ff0-af98-408e-2bd6953c36a2', 'Wix Chat', true],
        [SAMPLE_APP_IDS.popups, 'Puddlejump Pop-ups', false],
        [SAMPLE_APP_IDS.wheel, 'Lantern Spin Wheel', false],
        [SAMPLE_APP_IDS.bundles, 'Wickworks Bundles', false]
      ]),
      embeds,
      studio: false,
      requestUrl: url,
      baseUrl: base
    });
    return `${head}
<body class='' >
<script type="text/javascript">
    var bodyCacheable = true;
</script>
    <!--pageHtmlEmbeds.bodyStart start-->
    <script type="wix/htmlEmbeds" id="pageHtmlEmbeds.bodyStart start"></script>
    <script type="wix/htmlEmbeds" id="pageHtmlEmbeds.bodyStart end"></script>
    <!--pageHtmlEmbeds.bodyStart end-->
<script id="wix-first-paint">
    window.wixFirstPaint = 0;
</script>
<div id="SITE_CONTAINER">
<header id="SITE_HEADER">
<h1 class="font_0 wixui-rich-text__text" style="font-size:28px;"><span class="wixui-rich-text__text">${esc(store)}</span></h1>
<nav aria-label="Site"><a href="${base}">Home</a> <a href="${base}/category/all-products">Shop all</a> <a href="${base}/about">Our story</a> <a href="${base}/cart-page" aria-label="Cart, 0 items"><svg viewBox="0 0 24 24"><path d="M3 4h2l2 12h11"/></svg></a></nav>
</header>
<main id="PAGES_CONTAINER">
<h1 class="sUBnBkv oXQMDeL---typography-11-runningText" aria-hidden="false" data-hook="product-title">${esc(name)}</h1>
<p data-hook="formatted-primary-price">$28.00</p>
<wow-image class="zKjFJx sqVZMPS" data-image-info="{&quot;displayMode&quot;:&quot;fill&quot;,&quot;isLQIP&quot;:true,&quot;imageData&quot;:{&quot;width&quot;:1200,&quot;height&quot;:1200,&quot;uri&quot;:&quot;${hero[0]}_${hero[1]}~mv2.${hero[2]}&quot;}}" data-has-ssr-src="true"><img src="${media(hero[0], hero[1], hero[2], null, 'fill/w_73,h_73,al_c,q_85,usm_0.66_1.00_0.01,blur_2,enc_avif,quality_auto')}" alt="${esc(name)}" style="width:100%;height:100%;object-fit:cover" width="1200" height="1200"/></wow-image>
<img src="${media(hero[0], hero[1], hero[2], null, 'fill/w_50,h_50,al_c,q_85,enc_avif,quality_auto')}" alt="Thumbnail: ${esc(name)}" width="50" height="50" data-hook="thumbnail-image"/>
<div data-hook="description"><p>${esc(template)}</p></div>
<h2 class="font_2 wixui-rich-text__text">Poured by hand in small batches</h2>
<img loading="lazy" src="${media('a1b2c3', 'b0a1c2d3e4f5061728394a5b6c7d8e9f', 'jpg', 'candle-pour-final-v2.jpg', 'fill/w_640,h_420,al_c,q_80,enc_avif,quality_auto')}" alt="candle-pour-final-v2.jpg" width="640" height="420"/>
<h2 class="sEaR4JH" data-hook="SliderGalleryTitleDataHook.Title">You might also like</h2>
${related.map(([slug, label, hash]) => `<a href="${base}/product-page/${slug}"><img loading="lazy" src="${media('a1b2c3', hash, 'jpg', null, 'fill/w_49,h_49,al_c,q_85,blur_2,enc_avif,quality_auto')}" alt="" style="object-fit:cover"/><span>${esc(label)}</span></a>`).join('\n')}
</main>
<footer id="SITE_FOOTER">
<h3 class="font_3 wixui-rich-text__text">Help</h3>
<a href="${base}/shipping-returns">Shipping &amp; returns</a> <a href="https://juniperandoak.wixsite.com/shop/faq">FAQ</a> <a href="${base}/contact">Contact us</a>
<img loading="lazy" src="${media('a1b2c3', 'c0ffee00c0ffee00c0ffee00c0ffee00', 'png', 'IMG_0452.png', 'fill/w_120,h_40,al_c,q_85,enc_avif,quality_auto')}" alt="Juniper &amp; Oak logo" width="120" height="40"/>
</footer>
</div>
    <!--pageHtmlEmbeds.bodyEnd start-->
    <script type="wix/htmlEmbeds" id="pageHtmlEmbeds.bodyEnd start"></script>
${forms}
    <script type="wix/htmlEmbeds" id="pageHtmlEmbeds.bodyEnd end"></script>
    <!--pageHtmlEmbeds.bodyEnd end-->
<script src="https://static.parastorage.com/services/wix-thunderbolt/dist/main.4b9d5f8e.bundle.min.js" async></script>
<script type="application/json" id="wix-viewer-model">${model}</script>
</body>
</html>
`;
  }

  function fineSample() {
    const store = 'Northfield Tea Supply';
    const base = 'https://www.northfieldtea.example';
    const url = SAMPLES.fine.url;
    const name = 'Lapsang Souchong Loose Leaf Tea';
    const description = 'Smoky, pine-dried black tea from Fujian. A 100 g pouch makes about 40 cups. Ships in 1–2 days from our Vermont shop.';
    const hero = ['d4e5f6', '2b7e151628aed2a6abf7158809cf4f3c', 'jpg'];
    const heroUrl = media(hero[0], hero[1], hero[2], 'file.jpg', 'fit/w_500,h_500,q_90');
    const wixProduct = JSON.stringify({
      '@context': 'https://schema.org/', '@type': 'Product', name, description, sku: 'NTS-LS-100',
      image: [{ '@type': 'ImageObject', height: '1600', width: '1600', contentUrl: heroUrl, representativeOfPage: true, thumbnail: { '@type': 'ImageObject', height: '50', width: '50', contentUrl: media(hero[0], hero[1], hero[2], 'file.jpg', 'fit/w_50,h_50,q_90') } }],
      offers: { '@type': 'Offer', url, priceCurrency: 'USD', price: '14.50', availability: 'https://schema.org/InStock', seller: { '@type': 'Organization', name: store, sameAs: base } }
    });
    const notice = '<script>/* Shows a holiday shipping notice at the top of every page */</script>';
    const head = pageHead({
      title: `${name} | ${store}`,
      description,
      canonical: url,
      siteName: store,
      og: { description, image: heroUrl, price: '14.50', currency: 'USD' },
      ld: [wixProduct],
      embedsHead: '',
      essential: { site: { externalBaseUrl: base, isSEO: false }, requestUrl: url, language: { userLanguage: 'en' } },
      modelSite: { prefix: 'd4e5f6', icon: '3c4b5a69788796a5b4c3d2e1f00f1e2d' }
    });
    const model = viewerModel({
      apps: STORE_APPS.concat([
        ['225dd912-7dea-4738-8688-4b8c6955ffc2', 'Wix Forms', true],
        [SAMPLE_APP_IDS.reviews, 'Kettlebrook Reviews', false]
      ]),
      embeds: [{ name: 'Holiday shipping notice', position: 'bodyStart', html: notice }],
      studio: true,
      requestUrl: url,
      baseUrl: base
    });
    return `${head}
<body class='responsive' >
<script type="text/javascript">
    var bodyCacheable = true;
</script>
    <!--pageHtmlEmbeds.bodyStart start-->
    <script type="wix/htmlEmbeds" id="pageHtmlEmbeds.bodyStart start"></script>
${notice}
    <script type="wix/htmlEmbeds" id="pageHtmlEmbeds.bodyStart end"></script>
    <!--pageHtmlEmbeds.bodyStart end-->
<div id="SITE_CONTAINER">
<header id="SITE_HEADER" wix="[object Object]">
<a href="${base}"><img src="${media('d4e5f6', '0a0b0c0d0e0f0a1b2c3d4e5f6a7b8c9d', 'png', 'northfield-logo.png', 'fill/w_160,h_48,al_c,q_85,enc_avif,quality_auto')}" alt="Northfield Tea Supply" width="160" height="48"/></a>
<nav aria-label="Site"><a href="${base}/category/all-teas">All teas</a> <a href="${base}/brewing-guide">Brewing guide</a> <a href="${base}/about">About</a></nav>
</header>
<main id="PAGES_CONTAINER">
<section data-testid="section-container">
<h1 class="sUBnBkv" data-hook="product-title">${esc(name)}</h1>
<p data-hook="formatted-primary-price">$14.50</p>
<wow-image data-image-info="{&quot;isLQIP&quot;:true,&quot;imageData&quot;:{&quot;width&quot;:1600,&quot;height&quot;:1600,&quot;uri&quot;:&quot;${hero[0]}_${hero[1]}~mv2.${hero[2]}&quot;}}" data-has-ssr-src="true"><img src="${media(hero[0], hero[1], hero[2], null, 'fill/w_73,h_73,al_c,q_85,blur_2,enc_avif,quality_auto')}" alt="${esc(name)}" width="1600" height="1600"/></wow-image>
<img src="${media(hero[0], hero[1], hero[2], null, 'fill/w_50,h_50,al_c,q_85,enc_avif,quality_auto')}" alt="Thumbnail: ${esc(name)}" width="50" height="50"/>
<div data-hook="description"><p>${esc(description)}</p></div>
<h2 class="font_2 wixui-rich-text__text">How to brew it</h2>
<p>Use 3 g of leaves for 250 ml of water just off the boil. Steep for 3 to 4 minutes.</p>
<picture><img loading="lazy" src="${media('d4e5f6', '5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b', 'jpg', 'IMG_4471.jpg', 'fill/w_640,h_420,al_c,q_80,enc_avif,quality_auto')}" alt="IMG_4471.jpg" width="640" height="420"/></picture>
<h2 class="font_2 wixui-rich-text__text">Tasting notes</h2>
<p>Pine smoke, dried longan and a long, sweet finish.</p>
</section>
</main>
<footer id="SITE_FOOTER">
<h2 class="font_2 wixui-rich-text__text">Questions?</h2>
<a href="${base}/shipping">Shipping</a> <a href="${base}/contact">Contact us</a>
</footer>
</div>
    <!--pageHtmlEmbeds.bodyEnd start-->
    <script type="wix/htmlEmbeds" id="pageHtmlEmbeds.bodyEnd start"></script>
    <script type="wix/htmlEmbeds" id="pageHtmlEmbeds.bodyEnd end"></script>
    <!--pageHtmlEmbeds.bodyEnd end-->
<script src="https://static.parastorage.com/services/wix-thunderbolt/dist/main.4b9d5f8e.bundle.min.js" async></script>
<script type="application/json" id="wix-viewer-model">${model}</script>
</body>
</html>
`;
  }

  // ---- Markup shared by the server render and the browser ---------------------------------

  const guideHref = href => (href.startsWith('/') ? `${ORIGIN}${href}` : href);

  // Our own copy keeps short hyphenated words ("pop-ups", "sign-up") and a
  // number range with its unit ("8 MB", "55–70 characters") on one line, so narrow phones
  // don't break them. Visitor text and addresses are left to wrap anywhere.
  function softText(value) {
    return esc(value).replace(/\d[\d.,]*(?:–\d[\d.,]*)?\s(?:MB|KB|px|characters)\b|[A-Za-z0-9]+(?:-[A-Za-z0-9]+)+/g, match => (match.length > 24 ? match : `<span class="wpc-nowrap">${match}</span>`));
  }

  function richHtml(parts) {
    return asArray(parts).map(part => {
      if (part == null || part === false) return '';
      if (typeof part === 'string' || typeof part === 'number') return softText(part);
      if (part.code != null) return `<code>${esc(part.code)}</code>`;
      if (part.q != null) return `<q class="wpc-quote">${esc(part.q)}</q>`;
      if (part.b != null) return `<strong>${esc(part.b)}</strong>`;
      if (part.a != null) return `<a href="${esc(part.href)}">${esc(part.a)}</a>`;
      if (part.path) return pathHtml(part.path);
      if (part.mark) return `<code class="wpc-json">${esc(part.mark.before)}<mark>${esc(part.mark.at || ' ')}</mark>${esc(part.mark.after)}</code>`;
      return '';
    }).join('');
  }

  function richText(parts) {
    return asArray(parts).map(part => {
      if (part == null || part === false) return '';
      if (typeof part === 'string' || typeof part === 'number') return String(part);
      if (part.code != null) return part.code;
      if (part.q != null) return `“${part.q}”`;
      if (part.b != null) return part.b;
      if (part.a != null) return `${part.a} (${guideHref(part.href)})`;
      if (part.path) return part.path.join(' > ');
      if (part.mark) return `${part.mark.before}${part.mark.at}${part.mark.after}  (problem ${part.mark.at ? `at “${part.mark.at}”` : 'at the end'})`;
      return '';
    }).join('');
  }

  // A click path: each label kept whole, lines break only between steps.
  function pathHtml(path) {
    return `<span class="wpc-path">${path.map(step => `<span class="wpc-step">${esc(step)}</span>`).join('\u00a0<span class="wpc-sep" aria-hidden="true">›</span><span class="wpc-sr">, then</span> ')}</span>`;
  }

  function stepHtml(step) {
    const who = step.who ? `<strong class="wpc-who">${esc(step.who)}:</strong> ` : '';
    const body = step.path ? `${pathHtml(step.path)}${step.then ? `, then ${richHtml(step.then)}` : '.'}` : richHtml(step.text);
    return `<li>${who}${body}</li>`;
  }

  function stepText(step) {
    const who = step.who ? `${step.who}: ` : '';
    const body = step.path ? `${step.path.join(' > ')}${step.then ? `, then ${richText(step.then)}` : '.'}` : richText(step.text);
    return `${who}${body}`;
  }

  // Real table semantics, with explicit roles because phones restyle the
  // three-column tables as stacked rows.
  function tableHtml(table, id) {
    const head = `<thead role="rowgroup"><tr role="row">${table.cols.map(col => `<th scope="col" role="columnheader">${esc(col)}</th>`).join('')}</tr></thead>`;
    const rows = list => list.map(row => `<tr role="row">${row.map((cell, index) => (index === 0 ? `<th scope="row" role="rowheader">${richHtml(cell)}</th>` : `<td role="cell"><span class="wpc-cell-label" aria-hidden="true">${esc(table.cols[index])}: </span>${richHtml(cell)}</td>`)).join('')}</tr>`).join('');
    const tag = (caption, list) => `<table class="wpc-table" role="table" data-cols="${table.cols.length}"><caption class="wpc-sr">${esc(caption)}</caption>${head}<tbody role="rowgroup">${rows(list)}</tbody></table>`;
    const main = table.rows.length ? tag(table.caption, table.rows) : '';
    const more = asArray(table.more).map(section => `<details class="wpc-more-rows"><summary><span>${esc(section.label)}</span>${ICON}</summary>${tag(section.label, section.rows)}</details>`).join('');
    return `<div class="wpc-table-wrap" id="${esc(id)}-table">${main}${more}</div>`;
  }

  const ICON = '<span class="wpc-disclosure-icon" aria-hidden="true"></span>';

  function linksHtml(links) {
    return links.length ? `<p class="wpc-related">Read more: ${links.map(([text, href]) => `<a href="${esc(href)}">${esc(text)}</a>`).join(' · ')}</p>` : '';
  }

  function sourcesHtml(keys) {
    const items = keys.filter(key => SOURCES[key]);
    return items.length ? `<p class="wpc-cite">Source${items.length > 1 ? 's' : ''}: ${items.map(key => `<a href="${esc(SOURCES[key][1])}">${esc(SOURCES[key][0])}</a>`).join(' · ')}</p>` : '';
  }

  function cardHtml(item) {
    const found = item.found.length ? `<div class="wpc-found">${item.found.map(line => `<p>${richHtml(line)}</p>`).join('')}</div>` : '';
    const table = item.table ? tableHtml(item.table, `wpc-${item.id}`) : '';
    const fix = item.fix.length ? `<details class="wpc-more" data-wpc-fix><summary><span>What to do in Wix</span>${ICON}</summary><div class="wpc-more-body"><ol class="wpc-steps">${item.fix.map(stepHtml).join('')}</ol>${linksHtml(item.links)}</div></details>` : '';
    const why = item.why.length ? `<details class="wpc-more"><summary><span>Why this matters</span>${ICON}</summary><div class="wpc-more-body">${item.why.map(paragraph => `<p>${richHtml(paragraph)}</p>`).join('')}${item.fix.length ? '' : linksHtml(item.links)}${sourcesHtml(item.sourceKeys)}</div></details>` : '';
    return `<li class="wpc-card" data-status="${item.status}" data-finding="${esc(item.id)}"><div class="wpc-card-head"><span class="wpc-badge"><span class="wpc-badge-icon" aria-hidden="true"></span>${STATUS_WORDS[item.status]}<span class="wpc-sr">:</span></span><h4 class="wpc-card-title">${softText(item.title)}</h4></div>${found}${table}${fix || why ? `<div class="wpc-disclosures">${fix}${why}</div>` : ''}</li>`;
  }

  const TYPE_WORDS = { home: 'home page', product: 'store product page', category: 'store category page', 'blog-post': 'blog post', page: 'page', unknown: 'page' };
  const SPECIAL_WORDS = { cart: 'cart page', checkout: 'checkout page', 'thank-you': 'thank-you page', members: 'members page', 'blog-tag': 'blog tag page', 'blog-archive': 'blog archive page' };

  function pageSentence(result) {
    const { meta } = result;
    if (meta.notAPage) return 'This looks like an error page, not one of your pages.';
    if (!meta.isWix) return 'This doesn’t look like a Wix page.';
    const what = SPECIAL_WORDS[meta.special] || TYPE_WORDS[meta.pageType] || 'page';
    return `Looks like a Wix ${what}${meta.editor === 'studio' ? ' made in Wix Studio' : ''}.`;
  }

  function groupCounts(findings) {
    return GROUPS.map(group => {
      const items = findings.filter(item => item.group === group.id);
      const fixes = items.filter(item => item.status === 'fix').length;
      const checks = items.filter(item => item.status === 'check').length;
      const words = [fixes ? `${fixes} to fix` : '', checks ? `${checks} to check` : ''].filter(Boolean).join(', ');
      return { group, items, problems: fixes + checks, words };
    });
  }

  // The result in two parts: the head (what the page is, the counts) and the
  // body (notices, jump links, the groups of findings). The page keeps its
  // toolbar between them, so the buttons survive a new check.
  function resultHead(result, { example = false } = {}) {
    const { meta, counts } = result;
    const sample = meta.sample ? SAMPLES[meta.sample] : null;
    const kicker = example ? 'Example result' : sample ? 'Sample result' : 'Your page';
    const label = sample ? `Made-up page: ${sample.store}` : '';
    const address = meta.checkUrl ? shortUrl(meta.checkUrl) : meta.canonicalUrl ? shortUrl(meta.canonicalUrl) : '';
    return `<p class="wpc-kicker">${esc(kicker)}${label ? `<span class="wpc-kicker-sep" aria-hidden="true"> · </span><span class="wpc-kicker-label">${esc(label)}</span>` : ''}</p>
        <h2 id="wpc-result-title" tabindex="-1">${softText(pageSentence(result))}</h2>
        ${address ? `<p class="wpc-address"><code>${esc(address).replace(/\//g, '/<wbr>')}</code></p>` : ''}
        ${example ? '<p class="wpc-example-note">A made-up page, checked by the same code. Paste your own page’s source to check it.</p>' : ''}
        <p class="wpc-counts">${[['fix', 'to fix'], ['check', 'to check'], ['fine', 'look fine']].map(([key, words]) => `<span class="wpc-count" data-status="${key}" data-zero="${counts[key] === 0}"><b>${counts[key]}</b> ${words}</span>`).join('')}</p>`;
  }

  function resultMarkup(result, options = {}) {
    return `<div class="wpc-result-head">${resultHead(result, options)}</div>${resultBody(result)}`;
  }

  function resultBody(result) {
    const { meta } = result;
    const sample = meta.sample ? SAMPLES[meta.sample] : null;
    const notices = (result.notices || []).concat(sample ? [{ kind: 'sample', text: 'Shortened: a real Wix page source is much longer. This one keeps the parts the checks read.' }] : []);
    const groups = groupCounts(result.findings).filter(entry => entry.items.length || (entry.group.id === 'apps' && !meta.isWix && !meta.notAPage));
    const groupHtml = groups.map(({ group, items, words }) => {
      const shown = items.filter(item => item.status === 'fix' || item.status === 'check' || (item.status === 'info' && item.pin));
      const folded = items.filter(item => !shown.includes(item));
      const passed = folded.filter(item => item.status === 'fine').length;
      const notes = folded.length - passed;
      const foldLabel = [passed ? `${plural(passed, 'check')} passed` : '', notes ? plural(notes, 'note') : ''].filter(Boolean).join(', ');
      const skipped = group.id === 'apps' && !meta.isWix ? '<p class="wpc-group-note">Skipped: this isn’t a Wix page, so it has no Wix app list.</p>' : '';
      return `<section class="wpc-group" id="wpc-group-${group.id}" aria-labelledby="wpc-group-${group.id}-title"><div class="wpc-group-head"><h3 id="wpc-group-${group.id}-title">${esc(group.title)}</h3><p class="wpc-group-count">${words || (items.length ? 'Nothing to fix' : '')}</p></div>${skipped}${shown.length ? `<ul class="wpc-cards">${shown.map(cardHtml).join('')}</ul>` : ''}${folded.length ? `<details class="wpc-passed" data-wpc-passed><summary><span>${esc(foldLabel)}</span>${ICON}</summary><ul class="wpc-cards">${folded.map(cardHtml).join('')}</ul></details>` : ''}</section>`;
    }).join('');
    const jumps = groups.filter(entry => entry.items.length).map(({ group, problems }) => `<li><a href="#wpc-group-${group.id}">${esc(group.short)}${problems ? ` <span class="wpc-jump-count"><span class="wpc-sr">, </span>${problems}<span class="wpc-sr"> to look at</span></span>` : ''}</a></li>`).join('');
    return `${notices.length ? `<ul class="wpc-notices">${notices.map(notice => `<li data-notice="${esc(notice.kind)}">${softText(notice.text)}</li>`).join('')}</ul>` : ''}
      ${jumps ? `<nav class="wpc-jumps" aria-label="Jump to a group"><ul>${jumps}</ul></nav>` : ''}
      <div class="wpc-groups">${groupHtml}</div>`;
  }

  // ---- Plain-text summary ------------------------------------------------------------

  function summaryText(result, { date } = {}) {
    const { meta, counts } = result;
    const sample = meta.sample ? SAMPLES[meta.sample] : null;
    const address = meta.checkUrl ? shortUrl(meta.checkUrl) : meta.canonicalUrl ? shortUrl(meta.canonicalUrl) : '';
    const lines = [];
    lines.push(`Wix page check${address ? `: ${address}` : ''}`);
    if (sample) lines.push(`Made-up page: ${sample.store}`);
    lines.push(pageSentence(result));
    lines.push(`${counts.fix} to fix, ${counts.check} to check, ${counts.fine} look fine.${date ? ` Checked ${date} with the myuser.ai Wix page checker (${ORIGIN}${PATH}).` : ''}`);
    (result.notices || []).forEach(notice => lines.push(`Note: ${notice.text}`));
    ['fix', 'check'].forEach(status => {
      const items = result.findings.filter(item => item.status === status);
      if (!items.length) return;
      lines.push('', status === 'fix' ? 'TO FIX' : 'TO CHECK');
      items.forEach((item, index) => {
        lines.push('', `${index + 1}. ${item.title}`);
        item.found.forEach(line => lines.push(`   ${richText(line)}`));
        if (item.table) {
          item.table.rows.slice(0, 12).forEach(row => lines.push(`   - ${row.map(cell => richText(cell)).join(' · ')}`));
        }
        if (item.fix.length) {
          lines.push('   What to do in Wix:');
          item.fix.forEach(step => lines.push(`   - ${stepText(step)}`));
        }
        item.links.forEach(([text, href]) => lines.push(`   Read more: ${text} (${guideHref(href)})`));
      });
    });
    if (!counts.fix && !counts.check) lines.push('', 'Nothing to fix or check on this page.');
    lines.push('', 'This summary holds no page source. A clean check doesn’t mean a page will rank.');
    return `${lines.join('\n')}\n`;
  }

  function announceText(result) {
    const { counts, meta } = result;
    if (meta.notAPage) return 'Checked. This looks like an error page, not one of your pages.';
    return `Checked. ${counts.fix} to fix, ${counts.check} to check, ${counts.fine} look fine.`;
  }

  // ---- For the /tools directory card: the sample's four most telling findings --------

  const TELLING = ['template-text', 'app-overlap-', 'leftover-', 'product-duplicate', 'jsonld-error', 'custom-code-tracking', 'h1-many', 'description-missing'];

  function directoryPreview() {
    const sample = SAMPLES.problems;
    const result = analyzePage(sampleSource('problems'), { url: sample.url, sample: sample.id });
    const rows = [];
    TELLING.forEach(prefix => {
      result.findings.filter(item => (prefix.endsWith('-') ? item.id.startsWith(prefix) : item.id === prefix)).forEach(item => {
        if (rows.length < 4 && !rows.some(row => row.title === item.title)) rows.push({ status: item.status, title: item.title });
      });
    });
    return { sampleName: sample.store, counts: { fix: result.counts.fix, check: result.counts.check, fine: result.counts.fine }, rows };
  }

  // ---- Page behaviour ----------------------------------------------------------------

  function init(root) {
    if (!root || root.dataset.wpcReady === 'true') return;
    root.dataset.wpcReady = 'true';
    const find = name => root.querySelector(`[data-wpc-${name}]`);
    const source = find('source');
    const address = find('address');
    const fileInput = find('file');
    const checkButton = find('check');
    const clearButton = find('clear');
    const counter = find('count');
    const sourceError = find('source-error');
    const addressError = find('address-error');
    const resultBox = find('result');
    const headBox = find('result-head');
    const panel = find('result-panel') || resultBox;
    const status = find('status');
    const announcer = find('announcement');
    const fallback = find('fallback');
    const fallbackText = find('fallback-text');
    const copyButton = find('copy');
    const downloadButton = find('download');
    if (!source || !resultBox || !checkButton) return;
    let result = null;
    let origin = 'example';
    let loadedSample = null;
    let fileName = '';
    let reported = false;
    let countTimer = 0;
    let announceTimer = 0;

    const toolEvent = name => document.dispatchEvent(new CustomEvent('myuser:tool-event', { detail: { name } }));
    const setStatus = message => { if (status) status.textContent = message; };
    const announce = message => {
      if (!announcer) return;
      clearTimeout(announceTimer);
      announcer.textContent = '';
      announceTimer = setTimeout(() => { announcer.textContent = message; }, 120);
    };

    function showError(box, field, message) {
      if (!box) return;
      box.textContent = message || '';
      box.hidden = !message;
      if (field) {
        if (message) field.setAttribute('aria-invalid', 'true');
        else field.removeAttribute('aria-invalid');
      }
    }

    function updateCount() {
      const bytes = source.value ? utf8Bytes(source.value) : 0;
      if (counter) counter.textContent = bytes ? `${sizeLabel(bytes)} pasted` : '';
      if (clearButton) clearButton.disabled = !source.value && !(address && address.value);
    }

    function setView(view) {
      root.dataset.wpcView = view;
      resultBox.querySelectorAll('[data-wpc-passed]').forEach(details => { details.open = view === 'all'; });
      root.querySelectorAll('input[name="wpc-view"]').forEach(input => { input.checked = input.value === view; });
    }

    function render(next, focus) {
      result = next;
      const example = origin === 'example';
      if (headBox) {
        headBox.innerHTML = resultHead(result, { example });
        resultBox.innerHTML = resultBody(result);
      } else resultBox.innerHTML = resultMarkup(result, { example });
      root.dataset.wpcState = example ? 'example' : origin === 'own' ? 'own' : 'sample';
      setView(root.dataset.wpcView || 'focus');
      // Bring the answer into view when it starts below the fold (phones,
      // where it sits under the form), and move focus to its heading.
      if (focus) {
        const heading = root.querySelector('#wpc-result-title');
        if (heading) {
          heading.focus({ preventScroll: true });
          const header = document.querySelector('.site-header');
          const offset = (header ? header.getBoundingClientRect().height : 0) + 12;
          const box = panel.getBoundingClientRect();
          if (box.top < offset || box.top > window.innerHeight * 0.6) {
            const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            const top = Math.max(0, box.top + window.pageYOffset - offset);
            try { window.scrollTo({ top, behavior: reduce ? 'auto' : 'smooth' }); } catch (_) { window.scrollTo(0, top); }
          }
        }
      }
      announce(announceText(result));
    }

    // Reads the box and checks it. `kind` is 'own' or a sample id.
    function check(kind, { focus = true } = {}) {
      hideFallback();
      setStatus('');
      // The box can change without an input event (autofill, a restored form).
      updateCount();
      const typed = address ? readAddress(address.value) : { ok: true, url: null };
      showError(addressError, address, typed.ok ? '' : typed.message);
      const next = analyzePage(source.value, { url: typed.ok && typed.url ? typed.url.href : '', sample: kind === 'own' ? null : kind, file: kind === 'own' && fileName ? fileName : null });
      const input = next.meta.input;
      if (input !== 'ok') {
        const messages = {
          empty: 'Paste a page’s source first. On a computer, open your live page and press Ctrl+U (⌥⌘U on a Mac).',
          'too-big': `This is ${overLimitLabel(next.meta.bytes)}. The checker reads up to 8 MB; a Wix page source is usually well under that.`,
          url: 'That’s a web address. The checker needs the page’s source: open the page, press Ctrl+U (⌥⌘U on a Mac), then copy everything.',
          'not-html': 'This doesn’t look like a page source. It should start with <!DOCTYPE html>.'
        };
        showError(sourceError, source, messages[input] || messages['not-html']);
        if (input === 'url' && address && !address.value) {
          address.value = source.value.trim().replace(/^view-source:/i, '');
          showError(sourceError, source, `${messages.url} We moved the address to “Page address”.`);
          source.value = '';
          updateCount();
        }
        source.focus();
        return;
      }
      showError(sourceError, source, '');
      origin = kind;
      render(next, focus);
      if (kind === 'own' && !reported) { reported = true; toolEvent('wix_page_checked'); }
    }

    function hideFallback() { if (fallback) fallback.hidden = true; }

    async function copySummary() {
      if (!result) return;
      const text = summaryText(result, { date: new Date().toISOString().slice(0, 10) });
      hideFallback();
      try {
        if (!navigator.clipboard || typeof navigator.clipboard.writeText !== 'function') throw new Error('Clipboard unavailable');
        await navigator.clipboard.writeText(text);
        setStatus('Summary copied.');
        toolEvent('wix_summary_copied');
      } catch (_) {
        if (!fallback || !fallbackText) { setStatus('Couldn’t copy automatically.'); return; }
        fallbackText.value = text;
        fallback.hidden = false;
        fallbackText.focus();
        fallbackText.select();
        try { fallbackText.setSelectionRange(0, text.length); } catch (__) { /* not selectable */ }
        let copied = false;
        try { copied = typeof document.execCommand === 'function' && document.execCommand('copy'); } catch (__) { copied = false; }
        if (copied) {
          fallback.hidden = true;
          if (copyButton) copyButton.focus();
          toolEvent('wix_summary_copied');
        }
        setStatus(copied ? 'Summary copied.' : 'Couldn’t copy automatically. The summary is selected in the box below, so copy it from there.');
      }
    }

    function downloadSummary() {
      if (!result) return;
      const day = new Date().toISOString().slice(0, 10);
      const text = summaryText(result, { date: day });
      const host = result.meta.checkUrl ? result.meta.checkUrl.hostname : result.meta.sample ? 'sample' : 'page';
      const name = `wix-page-check-${host.replace(/[^a-z0-9.-]+/gi, '-')}-${day}.txt`;
      const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      setStatus(`Saved ${name}.`);
    }

    // Any edit makes the box the visitor's own page, even if it began as a sample.
    source.addEventListener('input', () => {
      fileName = '';
      loadedSample = null;
      clearTimeout(countTimer);
      countTimer = setTimeout(updateCount, 120);
      if (sourceError && !sourceError.hidden && source.value) showError(sourceError, source, '');
    });
    // A paste is the moment to check: no extra click needed.
    source.addEventListener('paste', () => {
      setTimeout(() => {
        loadedSample = null;
        updateCount();
        if (source.value.trim()) check('own');
      }, 0);
    });
    checkButton.addEventListener('click', () => check(loadedSample || 'own'));
    if (address) {
      // A new address re-checks what is already in the box.
      address.addEventListener('change', () => {
        if (source.value.trim() && origin !== 'example') check(loadedSample || 'own', { focus: false });
        else {
          const typed = readAddress(address.value);
          showError(addressError, address, typed.ok ? '' : typed.message);
        }
        updateCount();
      });
      address.addEventListener('input', () => { if (addressError && !addressError.hidden) showError(addressError, address, ''); });
    }
    if (clearButton) {
      clearButton.addEventListener('click', () => {
        source.value = '';
        if (address) address.value = '';
        if (fileInput) fileInput.value = '';
        fileName = '';
        loadedSample = null;
        showError(sourceError, source, '');
        showError(addressError, address, '');
        hideFallback();
        setStatus('');
        updateCount();
        origin = 'example';
        render(analyzePage(sampleSource('problems'), { url: SAMPLES.problems.url, sample: 'problems' }), false);
        source.focus();
      });
    }
    root.addEventListener('click', event => {
      const button = event.target.closest('[data-wpc-sample]');
      if (!button) return;
      const sample = SAMPLES[button.dataset.wpcSample];
      if (!sample) return;
      source.value = sampleSource(sample.id);
      if (address) address.value = sample.url;
      fileName = '';
      loadedSample = sample.id;
      updateCount();
      toolEvent('wix_page_sample_opened');
      check(sample.id);
    });
    if (fileInput) {
      fileInput.addEventListener('change', () => {
        const file = fileInput.files && fileInput.files[0];
        if (!file) return;
        if (file.size > MAX_BYTES) {
          showError(sourceError, source, `This file is ${overLimitLabel(file.size)}. The checker reads files up to 8 MB.`);
          fileInput.value = '';
          return;
        }
        const done = text => {
          source.value = text;
          fileName = file.name;
          loadedSample = null;
          updateCount();
          check('own');
        };
        if (typeof file.text === 'function') file.text().then(done, () => showError(sourceError, source, 'This file couldn’t be read. Try View page source instead.'));
        else {
          const reader = new FileReader();
          reader.onload = () => done(String(reader.result || ''));
          reader.onerror = () => showError(sourceError, source, 'This file couldn’t be read. Try View page source instead.');
          reader.readAsText(file);
        }
      });
    }
    root.addEventListener('change', event => {
      if (event.target.name === 'wpc-view') setView(event.target.value);
    });
    if (copyButton) copyButton.addEventListener('click', copySummary);
    if (downloadButton) downloadButton.addEventListener('click', downloadSummary);
    window.addEventListener('pagehide', () => { clearTimeout(countTimer); clearTimeout(announceTimer); });

    // "How to get a page's source" starts closed where the form and result
    // share one column (phones and tablets), so the example answer is near.
    const howTo = find('how');
    if (howTo && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 900px)').matches) howTo.open = false;
    // A browser restoring the form after Back brings a pasted source with it.
    updateCount();
    if (source.value.trim()) check('own', { focus: false });
    else {
      // The first paint already shows the example; keep its result so Copy
      // and Download work before anything is pasted.
      result = analyzePage(sampleSource('problems'), { url: SAMPLES.problems.url, sample: 'problems' });
      setView('focus');
    }
  }

  return Object.freeze({
    META,
    GROUPS,
    SAMPLES,
    SOURCES,
    MAX_BYTES,
    analyzePage,
    parseLite,
    liteView,
    domView,
    collect,
    jsonProblem,
    readAddress,
    pageKind,
    templateText,
    altIsFileName,
    humanFileName,
    cutToWidth,
    sampleSource,
    resultMarkup,
    resultHead,
    resultBody,
    summaryText,
    announceText,
    pageSentence,
    sizeLabel,
    utf8Bytes,
    directoryPreview,
    esc,
    init
  });
});
